<template>
  <div class="page-container">
    <div id="amap" ref="mapBox" class="mapContainer"></div>
  </div>
</template>

<script>
// AMapUI 为高德 AMapUI（PathSimplifier）全局对象，由外部脚本注入（非 import）。
/* global AMapUI */

// 轨迹数据（来自 transportService 的转换数据）
import trajectoryTrackData from '../transportService/trajectoryData-track';
import { locateJointPlayback, jointNavigatorTimeRatio } from '../in-transit/jointPlayback';
// 轨迹点解析/时间格式化/状态文案映射工具
import { parseTracePoints, formatTime, getGpsStatusConfig } from './utils';
import coordtransform from '@/WGS84.js';
import { isLikelyRawLonLatPairText, resolveAddressByCoord as resolveAddressByCoordUtil, normalizeCoordKey, isValidResolvedAddress } from '@/utils/coordAddressResolver'
import { loadAmapSDK, setAmapSecurityConfig } from '@/utils/amapConfig'
import { loadAmapUIScript } from '@/utils/loadAmapUI'
import {
  destroyAmapMapInstance,
  refreshTrajectoryPlugInMap
} from '@/utils/amapMapKeepAlive'
import {
  applySpeedModePathSimplifierTolerance,
  bindSpeedTrajectoryMap,
  buildTrajectoryHoverTitle,
  clearSpeedTrajectoryPolylines,
  getSpeedNavPathIndex,
  getSpeedPathStyle,
  resolveTracePointIndexFromEvent,
  syncSpeedTrajectoryMap
} from '@/utils/speedTrajectoryMap'
import {
  clipLocalToFullRatio,
  fullRatioToClipLocal,
  getPlaybackClipIndices,
  isFullPlaybackClip,
  isParkingInPlaybackClip,
  normalizeParkingEventTimeMs,
  mapNavigatorCursorToTraceIndex,
  slicePointsByRatioRange
} from '@/utils/playbackRangeClip'
import {
  schedulePlaybackRangeEndpointGeocode,
  cancelPlaybackRangeEndpointGeocode,
  isPlaybackRangeGeocodeGenerationStale
} from '@/utils/playbackRangeGeocodeDebounce'
import {
  createTrackMarkerIcon,
  refreshTrackOverlayMarkerIcons,
  TRACK_ENDPOINT_ICON_W,
  TRACK_ENDPOINT_ICON_H
} from '@/utils/amapTrackMarkerIcon'
import {
  TRAJECTORY_COLOR_MODE_SPEED,
  getTrajectoryColorSettings,
  isSpeedTrajectoryColorMode,
  onTrajectoryColorSettingsChange
} from '@/utils/trajectoryColorSettings'
// 轨迹导航车辆图标
import carIcon from '@/assets/lv.png';
// 起点图标
import startIcon from '@/assets/qi.png';
// 终点图标
import endIcon from '@/assets/zhong.png';
// 停车点图标
import stopIcon from '@/assets/ting.png';
import iconZhuang from '@/assets/icon_guiji_zhuang.svg';
import iconXie from '@/assets/icon_guiji_xie.svg';
import { codeToText, regionData } from 'element-china-area-data';

/** PathSimplifier 隐藏主轨迹线样式（与 trajectory.vue 一致） */
const PATH_LINE_HIDE = Object.freeze({
  lineWidth: 0,
  fillStyle: 'rgba(0,0,0,0)',
  strokeStyle: 'rgba(0,0,0,0)',
  borderWidth: 0,
  borderStyle: 'rgba(0,0,0,0)',
  dirArrowStyle: false
})

/** 主轨迹 / PathNavigator 已驶过段 / 选中态线宽一致，避免前后段粗细不同 */
const TRAJECTORY_PATH_LINE_WIDTH = 3

// 区县名 -> 行政区 code 缓存（用于把“荔城区”补全成“福建省莆田市荔城区”）
let __AREA_NAME_TO_CODE_CACHE__ = null;
function getAreaNameToCodeIndex() {
  if (__AREA_NAME_TO_CODE_CACHE__) return __AREA_NAME_TO_CODE_CACHE__;
  const map = new Map();
  try {
    Object.keys(codeToText || {}).forEach((code) => {
      const name = codeToText[code];
      if (!name) return;
      const key = String(name).trim();
      if (!key) return;
      // 同名时优先更细粒度（区县 code 不是以 0000/00 结尾）
      const prev = map.get(key);
      if (!prev) {
        map.set(key, code);
        return;
      }
      const isPrevFine = !String(prev).endsWith('0000') && !String(prev).endsWith('00');
      const isCurFine = !String(code).endsWith('0000') && !String(code).endsWith('00');
      if (!isPrevFine && isCurFine) map.set(key, code);
    });
  } catch (e) {
    // ignore
  }
  __AREA_NAME_TO_CODE_CACHE__ = map;
  return map;
}

let __AREA_CODE_TO_FULL_TEXT_CACHE__ = null;
function getAreaCodeToFullTextIndex() {
  if (__AREA_CODE_TO_FULL_TEXT_CACHE__) return __AREA_CODE_TO_FULL_TEXT_CACHE__;
  const map = new Map();
  const walk = (nodes, pathNames) => {
    const list = Array.isArray(nodes) ? nodes : [];
    for (const node of list) {
      if (!node) continue;
      const code = node.value != null ? String(node.value).trim() : "";
      const label = node.label != null ? String(node.label).trim() : "";
      const nextPath = label ? [...pathNames, label] : [...pathNames];
      if (code) {
        const text = nextPath.join("");
        if (text) map.set(code, text);
      }
      if (Array.isArray(node.children) && node.children.length) {
        walk(node.children, nextPath);
      }
    }
  };
  try {
    walk(regionData, []);
  } catch (e) {
    // ignore
  }
  __AREA_CODE_TO_FULL_TEXT_CACHE__ = map;
  return map;
}

export default {
  // 组件名（用于 Vue DevTools / keep-alive / 以及 ESLint multi-word 校验）
  name: 'TrackTrajectoryPage',
  props: {
    qualityMode: { type: Boolean, default: false },
    vehiclePositions: { type: Array, default: () => [] },
    selectedVehicleKey: { type: String, default: '' },
    qualityRestoreTime: { type: Number, default: null },
    trackData: {
      type: Object,
      default: null
    },
    useDefaultData: {
      type: Boolean,
      default: true
    },
    parkMinsThreshold: {
      type: Number,
      default: null
    },
    displayPrimaryId: {
      type: String,
      default: ""
    },
    colorChange:{
      tyoe:String,
      default: "#2FAE55,#98E0AD,#1E7B3B"
    },
    /** 装卸图标定位覆盖：{ startAreaCode, endAreaCode } */
    searchAreaCodeOverride: {
      type: Object,
      default: null
    },
    /** 起终点文案覆盖：{ start:{lon,lat,location,time}, end:{...} } */
    endpointDisplayOverride: {
      type: Object,
      default: null
    },
    /** type=detail 等场景：不展示搜索条件装/卸定位图标 */
    hideSearchEndpointMarkers: {
      type: Boolean,
      default: false
    },
    /** 运单尚未到计划结束时间时，末次定位仅显示车辆图标。 */
    hideTrackEndMarker: {
      type: Boolean,
      default: false
    },
    /** 运单车辆信息窗复用已格式化的监控数据。 */
    vehicleMonitorInfo: {
      type: Object,
      default: null
    },
    /** 查询/加载轨迹后默认不自动打开点位 InfoWindow；点击轨迹点/停车点仍可打开。 */
    suppressAutoInfoWindow: {
      type: Boolean,
      default: true
    },
    /** 预估轨迹颜色；未传入时沿用当前轨迹配色。 */
    estimateRouteColor: {
      type: String,
      default: ''
    },
    /** 嵌入运单地图时固定使用单色连续轨迹线，不受全局速度分段配色影响。 */
    forceSolidTrackColor: {
      type: Boolean,
      default: false
    },
    /** 嵌入地图仅展示轨迹线，轨迹点仍可通过轨迹线点击查看。 */
    hideTrackPoints: {
      type: Boolean,
      default: false
    },
    /** 静态运单地图不展示轨迹导航车，由页面的最新定位车辆统一展示。 */
    hideNavigatorVehicle: {
      type: Boolean,
      default: false
    },
    /** 静态运单地图点击轨迹点或停车点时，不把车辆预览移动到历史位置。 */
    disablePointPreviewVehicle: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // AMap 构造器（与 LeftJoin 等同源 loadAmapSDK，禁止与 $createAMap/jsapi-loader 混用）
      AMap: null,
      // 地图实例（new AMap.Map）
      mapInstance: null,
      // 地理编码实例（用于逆地理解析地址）
      geocoder: null,
      // 信息窗实例（展示轨迹点信息）
      infoWindow: null,
      // AMapUI.load 回调里的 PathSimplifier 构造函数（不会自动挂到 AMapUI.PathSimplifier）
      pathSimplifierCtor: null,
      // AMapUI PathSimplifier 实例（用于渲染轨迹线、点事件）
      pathSimplifierIns: null,
      // 路径导航器（用于沿轨迹移动、取当前位置、游标等）
      navg: null,
      // 停车点 Marker 图标实例
      markerStopIcon: null,
      // 起点 Marker 实例
      markerStart: null,
      // 终点 Marker 实例
      markerEnd: null,
      // 搜索条件始发地（装）Marker
      searchOriginMarker: null,
      // 搜索条件目的地（卸）Marker
      searchDestMarker: null,
      // 装/卸点文案
      searchOriginText: '',
      searchDestText: '',
      // estimateRoute 剩余轨迹虚线
      estimateRoutePolyline: null,
      estimateRouteVisible: false,
      // 起点/终点底部信息条（更新时间 + 地址）
      markerStartLabel: null,
      markerEndLabel: null,
      /** 与 trajectory.vue 一致：工具栏隐藏起终点文字条 */
      endpointMarkersHidden: false,
      /** 仅数据点 / 隐藏主轨迹线（与 trajectory.vue「上报位置」一致） */
      hidePathLineOnly: false,
      /** 隐藏停车点 Marker（与 trajectory.vue 一致） */
      parkingMarkersHidden: false,
      /** 隐藏主轨迹线前 PathSimplifier 样式备份 */
      _pathLineBk: null,
      /** 信息窗最后一次 payload（用于隐藏停车点时关闭/恢复） */
      _lastInfoPayload: null,
      _parkingInfoRestore: null,
      // 与主站 RightDataNewTrack 一致：本页逆地理结果缓存（coordAddressResolver 先读本地再读全局）
      mapAddressCache: {},
      // 停车点 Marker 列表（用于批量 remove/add）
      markerStopList: [],
      /** { marker, label, pointIndex } 供关联条裁剪显隐，避免拖动后反复 remove/add 导致图标缩放异常 */
      parkingMarkerRecords: [],
      // 查询解析后的搜索端点 GCJ [lng, lat]
      lastSearchOriginGcj: null,
      lastSearchDestGcj: null,
      // 页面入参（来自 query）
      formItem: {
        // 车牌号（当前实现：格式为 "vno,vco"；通过 currentVno 取逗号前部分）
        vno: "",
        // 车牌颜色
        vco: "",
        // 开始时间（此页不提供筛选 UI，但保留字段与原逻辑一致）
        startTime: "",
        // 结束时间
        endTime: ""
      },
      // 是否存在可播放（可渲染）轨迹
      isCanPlay: false,
      // 轨迹总距离（米）
      distanceAll: 0,
      // 播放进度（百分比；此页不渲染进度条 UI，但保留字段与原逻辑一致）
      sliderVal: 0,
      // 进度起始时间（格式化文本）
      barStartTime: "00:00",
      // 进度结束总时长（毫秒）
      barEndTimes: 0,
      // 进度结束时间（格式化文本）
      barEndTime: "00:00",
      // 播放状态：stop/start/pause（此页不提供播放 UI，但保留状态供事件逻辑使用）
      playStatus: "stop",
      // 倍速弹层开关（此页不提供 UI，但保留字段与原逻辑一致）
      speedMod: false,
      // 基础速度（PathNavigator 使用）
      baseSpeed: 100,
      // 倍速展示文案（此页不提供 UI，但保留字段与原逻辑一致）
      speedName: "倍速",
      // 倍速可选项
      speedList: [
        { label: "50.0X", value: 50 },
        { label: "20.0X", value: 20 },
        { label: "10.0X", value: 10 },
        { label: "5.0X", value: 5 },
        { label: "1.0X", value: 1 }
      ],
      // 轨迹点列表（parseTracePoints 后的结果）
      traceList: [],
      independentPositionMarkers: [],
      independentPositionSignature: '',
      jointVehicleMarker: null,
      jointPlayTimer: null,
      jointPlayRatio: 0,
      jointSpeed: 1,
      _unsubSpeedTraj: null,
      _unsubTrajColor: null,
      // 停车事件列表（用于渲染停车点 Marker）
      showTrackParkingDetailList: [],
      // 暂停时已行驶距离（用于点击轨迹点/停车点后定位）
      pauseDistance: 0,
      /** 点击轨迹数据点预览：导航器临时跳到该点，pauseDistance/进度条不变 */
      tracePointPreviewActive: false,
      /** 预览态临时车标（PathNavigator 仍停在真实播放位置） */
      previewVehicleMarker: null,
      // 当前轨迹路径缓存（用于动态刷新“更新时间”标题）
      currentTrackPath: [],
      // 当前已渲染的轨迹标题（避免同值重绘引发闪烁）
      currentTrackTitle: "",
      // 当前悬停点索引（避免重复悬停更新）
      hoverPointIndex: -1,
      // 当前悬停用于标题展示的“有效轨迹点”索引（需有 gtm）
      hoverUpdatePointIndex: -1,
      // 短时间忽略 mouseout（规避重绘触发抖动）
      suppressHoverOutUntil: 0,
      // 用户正在拖拽地图时，暂停自动聚焦，避免抢地图控制权
      mapUserDragging: false,
      /** 截图折叠左侧期间禁止 setFitView，避免突然放大 */
      mapViewFitLocked: false,
      // 自动聚焦节流，避免频繁 setCenter/panTo 造成抖动
      followFocusLastTs: 0,
      followFocusCooldownMs: 180,
      mapDragStartHandler: null,
      mapDragEndHandler: null,
      showSatellite: false,
      tileLayer: {
        satellite: null,
        roadNet: null
      },
      ruler: null,
      isRanging: false,
      mapReadyPromise: null,
      // 避免直接修改 prop：颜色使用本地副本覆盖
      colorChangeLocal: null,
      _trajectoryColorMode: getTrajectoryColorSettings().mode,
      speedColoredPolylines: [],
      speedColoredPointMarkers: [],
      speedColoredPointMarkerRegistry: null,
      /** 关联播放条裁剪：0–1，仅影响地图展示轨迹与起终点 */
      playbackClipStartRatio: 0,
      playbackClipEndRatio: 1,
      /** 与地图上起终点文字条第二行完全一致（供行程卡同步） */
      tripCardSyncedStartLine: '',
      tripCardSyncedEndLine: '',
      /** 避免 trackData 深度 watch 在切回页面时重复 handleSearch */
      _lastTrackDataSig: ''
    };
  },
  computed: {
    effectiveColorChange() {
      return this.colorChangeLocal || this.colorChange;
    }
  },
  async mounted() {
    this.initForm();
    await this.ensureMapReady();
    this._unsubSpeedTraj = bindSpeedTrajectoryMap(() => this.getSpeedTrajMapHost());
    this._unsubTrajColor = onTrajectoryColorSettingsChange((cfg) => {
      this.applyTrajectoryColorSettings(cfg)
    })
    if (this.hasRenderableTrackData()) {
      this._lastTrackDataSig = this.trackDataSignature(this.trackData);
      await this.handleSearch(true);
    }
    this.syncIndependentPositionMarkers();
  },
  beforeDestroy() {
    this.clearIndependentPositionMarkers()
    this.clearJointPlayback()
    cancelPlaybackRangeEndpointGeocode(this)
    if (this._unsubTrajColor) this._unsubTrajColor();
    if (this._unsubSpeedTraj) this._unsubSpeedTraj();
    clearSpeedTrajectoryPolylines(this.getSpeedTrajMapHost());
    this.unbindMapFollowGuards();
    this.cleanupMapTools();
    if (typeof window !== 'undefined' && window.__AMAP_MAP__ === this.mapInstance) {
      window.__AMAP_MAP__ = null;
    }
    destroyAmapMapInstance(this.mapInstance);
    this.mapInstance = null;
  },
  activated() {
    this.restoreMapAfterTabSwitch();
  },
  deactivated() {
    this.releaseMapForTabSwitch();
  },
  watch: {
    vehiclePositions: {
      deep: true,
      async handler() {
        await this.ensureMapReady()
        if (!this._isDestroyed) this.syncIndependentPositionMarkers()
      }
    },
    selectedVehicleKey(key) { this.focusIndependentVehicle(key) },
    trackData: {
      deep: true,
      async handler(val, oldVal) {
        if (!val || val === oldVal) return;
        const sig = this.trackDataSignature(val);
        if (sig && sig === this._lastTrackDataSig) return;
        this._lastTrackDataSig = sig;
        await this.ensureMapReady();
        this.handleSearch();
      }
    },
    'trackData.parkInfo.trackParkingDetailList': {
      deep: true,
      handler() { this.syncExternalParkingData() }
    },
    searchAreaCodeOverride: {
      deep: true,
      async handler() {
        if (this.hideSearchEndpointMarkers) {
          this.clearSearchEndpointMarkers()
          return
        }
        await this.ensureMapReady()
        await this.resolveSearchEndpointsForMap()
        this.renderSearchEndpointMarkers()
      }
    },
    hideSearchEndpointMarkers(val) {
      if (val) this.clearSearchEndpointMarkers()
    },
    hideTrackEndMarker() {
      this.syncEndpointMarkersVisibility()
    },
    hideNavigatorVehicle() {
      if (this.trackData && this.trackData.segmentKeys) {
        if (this.jointVehicleMarker) this.hideNavigatorVehicle ? this.jointVehicleMarker.hide() : this.jointVehicleMarker.show()
        return
      }
      if (this.pathSimplifierIns) this.rebuildNavigatorPassedStyle()
    },
    endpointDisplayOverride: {
      deep: true,
      handler() {
        this.applyEndpointDisplayOverride();
      }
    }
  },
  beforeUnmount() {
    this.unbindMapFollowGuards();
    this.cleanupMapTools();
    if (typeof window !== 'undefined' && window.__AMAP_MAP__ === this.mapInstance) {
      window.__AMAP_MAP__ = null
    }
  },
  methods: {
    /** 切页离开时：暂停播放并通知右侧同步为暂停态，保留 pauseDistance 供回来后恢复 */
    pausePlaybackForTabSwitch() {
      const wasPlaying = this.playStatus === 'start';
      let navPos = null;
      if (this.navg) {
        if (wasPlaying) {
          try {
            this.navg.pause();
          } catch (e) {
            /* ignore */
          }
        }
        try {
          const d = Number(this.navg.getMovedDistance());
          if (Number.isFinite(d) && d >= 0) this.pauseDistance = d;
        } catch (e) {
          /* ignore */
        }
        try {
          const pos = this.navg.getPosition && this.navg.getPosition();
          if (pos) {
            navPos = {
              lon: pos.lng != null ? pos.lng : pos[0],
              lat: pos.lat != null ? pos.lat : pos[1]
            };
          }
        } catch (e) {
          /* ignore */
        }
      }
      if (wasPlaying) {
        this.playStatus = 'pause';
      }
      const saved = Math.max(0, Number(this.pauseDistance) || 0);
      this._tabSwitchResumeDistance = saved;
      this._tabSwitchWasPaused = wasPlaying || this.playStatus === 'pause' || saved > 0;
      if (wasPlaying) {
        const navTotal =
          typeof this.getNavigatorDistanceAll === 'function' ? this.getNavigatorDistanceAll() : 0;
        const clipLocalRatio = navTotal > 0 ? saved / navTotal : 0;
        const fullRatio =
          typeof this.mapClipLocalToFullRatio === 'function'
            ? this.mapClipLocalToFullRatio(clipLocalRatio)
            : clipLocalRatio;
        this.$emit('nav-progress', {
          ratio: fullRatio,
          isPlaying: false,
          navPos: navPos || undefined
        });
      }
    },
    releaseMapForTabSwitch() {
      if (this.trackData && this.trackData.segmentKeys) this.clearJointPlayback()
      this.pausePlaybackForTabSwitch();
      this.unbindMapFollowGuards();
      this.cleanupMapTools();
      if (this.navg) {
        try {
          if (typeof this.navg.destroy === 'function') this.navg.destroy();
        } catch (e) {
          /* ignore */
        }
        this.navg = null;
      }
      if (typeof window !== 'undefined' && window.__AMAP_MAP__ === this.mapInstance) {
        window.__AMAP_MAP__ = null;
      }
      destroyAmapMapInstance(this.mapInstance);
      this.mapInstance = null;
      this.pathSimplifierIns = null;
      this.mapReadyPromise = null;
    },
    async restoreMapAfterTabSwitch() {
      const resumeDistance = Math.max(0, Number(this._tabSwitchResumeDistance) || 0);
      const shouldResumePause = !!this._tabSwitchWasPaused && resumeDistance > 0;
      this._tabSwitchResumeDistance = 0;
      this._tabSwitchWasPaused = false;
      try {
        await this.$nextTick();
        await this.ensureMapReady();
        if (this.hasRenderableTrackData()) {
          await this.handleSearch();
        }
        if (shouldResumePause && this.navg) {
          this.pauseDistance = resumeDistance;
          this.restoreNavigatorToPauseDistance(resumeDistance);
          this.playStatus = 'pause';
          try {
            const live = Number(this.navg.getMovedDistance());
            if (Number.isFinite(live) && live >= 0) this.pauseDistance = live;
          } catch (e) {
            /* ignore */
          }
          const navTotal =
            typeof this.getNavigatorDistanceAll === 'function' ? this.getNavigatorDistanceAll() : 0;
          const clipLocalRatio = navTotal > 0 ? this.pauseDistance / navTotal : 0;
          this.sliderVal =
            (typeof this.mapClipLocalToFullRatio === 'function'
              ? this.mapClipLocalToFullRatio(clipLocalRatio)
              : clipLocalRatio) * 100;
          this.emitNavProgressFromNavigator();
          const currentPos = this.navg.getPosition && this.navg.getPosition();
          this.$emit('nav-progress', {
            ratio: this.sliderVal / 100,
            isPlaying: false,
            navPos: currentPos
              ? {
                  lon: currentPos.lng != null ? currentPos.lng : currentPos[0],
                  lat: currentPos.lat != null ? currentPos.lat : currentPos[1]
                }
              : undefined
          });
        }
        this.$nextTick(() => {
          refreshTrajectoryPlugInMap(this);
        });
      } catch (e) {
        console.error('地图恢复失败', e);
      }
    },
    formatAreaCodeFullText(areaCode) {
      const code = areaCode != null ? String(areaCode).trim() : ''
      if (!/^\d{6}$/.test(code)) return (codeToText && codeToText[code]) ? codeToText[code] : ''
      // element-china-area-data 的 codeToText 只覆盖区县级，这里用 regionData 树反查完整省市区
      const idx = getAreaCodeToFullTextIndex()
      const full = idx.get(code)
      if (full) return full
      return (codeToText && codeToText[code]) ? codeToText[code] : ''
    },
    parseTrajectoryColorTriple(val) {
      const raw = val == null ? '' : String(val)
      const parts = raw.split(',').map((s) => s.trim()).filter(Boolean)
      const c1 = parts[0] || null
      const c2 = parts[1] || c1 || null
      const c3 = parts[2] || c2 || c1 || null
      return { c1, c2, c3 }
    },
    getSpeedTrajPathStyle(pathInfo) {
      const pathData = pathInfo && pathInfo.pathData
      return getSpeedPathStyle(
        pathData,
        TRAJECTORY_PATH_LINE_WIDTH,
        this.hidePathLineOnly
      )
    },
    formatTrajectoryHoverTitle(pathData, pathIndex, pointIndex) {
      return buildTrajectoryHoverTitle(pathData, pathIndex, pointIndex, {
        trackTitle: this.currentTrackTitle,
        totalPoints: this.traceList.length,
        pathSimplifierIns: this.pathSimplifierIns
      })
    },
    getTrajectoryColorTriple() {
      return this.parseTrajectoryColorTriple(this.effectiveColorChange)
    },
    normalizeEstimateRoutePoints(points) {
      const list = Array.isArray(points) ? points : []
      const out = []
      for (const p of list) {
        if (!p) continue
        const lon = p.lon != null ? p.lon : p.longitude
        const lat = p.lat != null ? p.lat : p.latitude
        const [x, y] = this.normalizeLonLatToGcj(lon, lat)
        if (Number.isFinite(x) && Number.isFinite(y) && !(x === 0 && y === 0)) {
          out.push([x, y])
        }
      }
      return out
    },
    clearEstimateRouteOverlay() {
      if (this.estimateRoutePolyline) {
        try {
          this.estimateRoutePolyline.setMap(null)
        } catch (e) {
          /* ignore */
        }
        this.estimateRoutePolyline = null
      }
      this.estimateRouteVisible = false
    },
    setEstimateRouteOverlay({ visible, estimateRoute } = {}) {
      this.estimateRouteVisible = !!visible
      const path = this.normalizeEstimateRoutePoints(estimateRoute)
      if (!this.mapInstance || !this.AMap || path.length < 2) {
        this.clearEstimateRouteOverlay()
        return
      }
      const { c3 } = this.getTrajectoryColorTriple()
      const estimateColor = this.estimateRouteColor || c3 || '#2FAE55'
      if (!this.estimateRoutePolyline) {
        this.estimateRoutePolyline = new this.AMap.Polyline({
          path,
          strokeColor: estimateColor,
          strokeWeight: 3,
          strokeOpacity: 0.95,
          strokeStyle: 'dashed',
          strokeDasharray: [8, 8],
          zIndex: 185
        })
      } else {
        this.estimateRoutePolyline.setPath(path)
        this.estimateRoutePolyline.setOptions({ strokeColor: estimateColor })
      }
      this.estimateRoutePolyline.setMap(this.estimateRouteVisible ? this.mapInstance : null)
    },
    getNavigatorSpeedMult() {
      const m = String(this.speedName || '').match(/([\d.]+)\s*X/i)
      if (m) {
        const n = Number(m[1])
        if (Number.isFinite(n) && n > 0) return n
      }
      const fallback = this.speedList && this.speedList[1] ? Number(this.speedList[1].value) : 20
      return Number.isFinite(fallback) && fallback > 0 ? fallback : 20
    },
    isPlaybackClipActive() {
      return !isFullPlaybackClip(this.playbackClipStartRatio, this.playbackClipEndRatio)
    },
    mapFullRatioToClipLocal(fullRatio) {
      return fullRatioToClipLocal(fullRatio, this.playbackClipStartRatio, this.playbackClipEndRatio)
    },
    mapClipLocalToFullRatio(localRatio) {
      return clipLocalToFullRatio(localRatio, this.playbackClipStartRatio, this.playbackClipEndRatio)
    },
    getNavigatorDistanceAll() {
      if (!this.isPlaybackClipActive() || !this.traceList.length || !this.AMap) {
        return this.distanceAll
      }
      const { sliced } = slicePointsByRatioRange(
        this.traceList,
        this.playbackClipStartRatio,
        this.playbackClipEndRatio
      )
      const path = sliced.map((p) => [p.lon, p.lat])
      if (path.length < 2) return 0
      return this.AMap.GeometryUtil.distanceOfLine(path)
    },
    resetNavigatorToClipWindowStart() {
      if (!this.navg || !this.pathSimplifierIns) return
      this.tracePointPreviewActive = false
      this.clearPreviewVehicleMarker()
      // 与 seek 一致：屏蔽 pause 回调，避免 stop/start/pause 误判为播完并回写终点比例
      this._suppressNavProgressEmit = true
      this._pathSeeking = true
      try {
        this.navg.stop()
        this.navg.start()
        this.navg.pause()
        this.navg.moveToPoint(0)
      } catch (e) {
        /* ignore */
      } finally {
        this._pathSeeking = false
        this._suppressNavProgressEmit = false
      }
      this.playStatus = 'pause'
      this.pauseDistance = 0
      const fullRatio = this.isPlaybackClipActive() ? this.playbackClipStartRatio : 0
      this.sliderVal = fullRatio * 100
      this.pathSimplifierIns.renderLater()
      this.syncInfoWindowFromNavigator()
    },
    /** 导航器游标（裁剪 path 局部下标）→ 全量 traceList 下标 */
    resolveNavigatorTraceIndex() {
      if (!this.navg || !this.traceList.length) return 0
      let cursorIdx = 0
      try {
        const cursor = this.navg.getCursor()
        cursorIdx = cursor && cursor.idx != null ? cursor.idx : 0
      } catch (e) {
        cursorIdx = 0
      }
      return mapNavigatorCursorToTraceIndex(
        this.traceList,
        cursorIdx,
        this.playbackClipStartRatio,
        this.playbackClipEndRatio
      )
    },
    /** 关联条复位/巡航后同步地图 info「更新时间」等字段 */
    syncInfoWindowFromNavigator() {
      if (this.hideNavigatorVehicle || !this.navg || !this.traceList.length) return
      try {
        const currentPos = this.navg.getPosition()
        if (!currentPos) return
        const normalData = this.getNormalizedData()
        this.syncTrackUpdateTime(normalData.normalInfo)
        const open =
          this.infoWindow &&
          typeof this.infoWindow.getIsOpen === 'function' &&
          this.infoWindow.getIsOpen()
        if (open) {
          this.showInfoWindow({
            lonlat: currentPos,
            infoData: {
              ...normalData.normalInfo,
              addr: normalData.normalLonLat,
              vno: this.getInfoPlateNo()
            }
          })
        }
      } catch (e) {
        /* ignore */
      }
    },
    rebuildNavigatorPassedStyle() {
      if (!this.pathSimplifierIns) return
      const prevPlayStatus = this.playStatus
      let restoreDistance = Math.max(0, Number(this.pauseDistance) || 0)
      if (prevPlayStatus === 'start' && this.navg) {
        try {
          const liveDistance = Number(this.navg.getMovedDistance())
          if (Number.isFinite(liveDistance) && liveDistance > restoreDistance) {
            restoreDistance = liveDistance
          }
        } catch (e) {
          /* ignore */
        }
      }
      const wasPlaying = prevPlayStatus === 'start'
      const speedMult = this.getNavigatorSpeedMult()
      if (this.navg) {
        try {
          if (typeof this.navg.off === 'function') {
            try { this.navg.off('move', this.handleNavgMove) } catch (e) { /* ignore */ }
            try { this.navg.off('pause', this.handleNavgPause) } catch (e) { /* ignore */ }
          }
        } catch (e) {
          /* ignore */
        }
        try {
          if (typeof this.navg.stop === 'function') this.navg.stop()
        } catch (e) {
          /* ignore */
        }
        try {
          if (typeof this.navg.destroy === 'function') this.navg.destroy()
        } catch (e) {
          /* ignore */
        }
      }
      this.navg = null

      try {
        this.navg = this.pathSimplifierIns.createPathNavigator(this.getNavigatorPathIndex(), {
          loop: false,
          speed: this.baseSpeed * speedMult,
          pathNavigatorStyle: {
            width: this.hideNavigatorVehicle ? 0 : 32,
            height: this.hideNavigatorVehicle ? 0 : 32,
            autoRotate: true,
            initRotateDegree: -90,
            strokeStyle: null,
            fillStyle: null,
            content: this.hideNavigatorVehicle ? '<div style="display:none"></div>' : this.getPathNavigatorCarImageContent(),
            pathLinePassedStyle: this.getNavigatorPathLinePassedStyle()
          }
        })
        this.navg.on('move', this.handleNavgMove)
        this.navg.on('pause', this.handleNavgPause)
        this.navg.start()
        this.navg.pause()
        this.playStatus = 'pause'
        if (restoreDistance > 0) {
          this.navg.moveByDistance(restoreDistance)
        } else {
          this.navg.moveToPoint(0)
        }
        this.pauseDistance = this.navg.getMovedDistance()
        const navTotal = this.getNavigatorDistanceAll()
        this.sliderVal = navTotal > 0
          ? this.mapClipLocalToFullRatio(this.pauseDistance / navTotal) * 100
          : 0
        if (wasPlaying) {
          this.navg.resume()
          this.playStatus = 'start'
        } else {
          this.playStatus = restoreDistance > 0 ? 'pause' : (prevPlayStatus === 'stop' ? 'stop' : 'pause')
        }
        this.pathSimplifierIns.renderLater()
      } catch (e) {
        /* ignore */
      }
    },
    seekNavigatorToGlobalIndex(globalIdx) {
      if (!this.navg || !Number.isFinite(Number(globalIdx)) || Number(globalIdx) < 0) return false
      const targetIdx = Math.min(Math.max(0, Math.round(Number(globalIdx))), this.traceList.length - 1)
      let navPointIdx = targetIdx
      if (this.isPlaybackClipActive()) {
        const { startIdx, endIdx } = getPlaybackClipIndices(
          this.traceList,
          this.playbackClipStartRatio,
          this.playbackClipEndRatio
        )
        navPointIdx = Math.min(endIdx - startIdx, Math.max(0, targetIdx - startIdx))
      }
      if (this.playStatus === 'start') {
        try { this.navg.pause() } catch (e) { /* ignore */ }
      }
      this.navg.stop()
      this.navg.start()
      this.navg.pause()
      this.playStatus = 'pause'
      if (navPointIdx === 0) {
        this.navg.moveToPoint(0)
      } else {
        this.navg.moveToPoint(navPointIdx)
      }
      this.pauseDistance = this.navg.getMovedDistance()
      const navTotal = this.getNavigatorDistanceAll()
      this.sliderVal = navTotal > 0
        ? this.mapClipLocalToFullRatio(this.pauseDistance / navTotal) * 100
        : 0
      this.pathSimplifierIns && this.pathSimplifierIns.renderLater()
      return true
    },
    clearPreviewVehicleMarker() {
      if (this.previewVehicleMarker) {
        try {
          this.previewVehicleMarker.setMap(null);
        } catch (e) {
          /* ignore */
        }
        this.previewVehicleMarker = null;
      }
    },
    showPreviewVehicleMarker(lon, lat, angleDeg) {
      const AMap = this.AMap || window.AMap;
      if (!AMap || !this.mapInstance || lon == null || lat == null) return;
      this.clearPreviewVehicleMarker();
      this.previewVehicleMarker = new AMap.Marker({
        position: [Number(lon), Number(lat)],
        zIndex: 96,
        icon: new AMap.Icon({
          size: new AMap.Size(40, 40),
          image: carIcon,
          imageSize: new AMap.Size(40, 40)
        }),
        offset: new AMap.Pixel(-20, -20),
        angle: angleDeg != null ? angleDeg : 0
      });
      this.previewVehicleMarker.setMap(this.mapInstance);
    },
    restoreNavigatorToPauseDistance(pauseDistance) {
      if (!this.navg) return;
      const d = Math.max(0, Number(pauseDistance) || 0);
      this.navg.stop();
      this.navg.start();
      this.navg.pause();
      if (d <= 0) {
        this.navg.moveToPoint(0);
      } else {
        this.navg.moveByDistance(d);
      }
    },
    async showPreviewPointInfoWindow(pointIdx, previewLon, previewLat) {
      if (!this.traceList || !this.traceList.length) return;
      const AMap = this.AMap || window.AMap;
      if (!AMap || !this.mapInstance) return;
      const idx = Math.max(0, Math.min(Number(pointIdx) || 0, this.traceList.length - 1));
      const point = this.traceList[idx];
      if (!point) return;
      const lon = Number(previewLon);
      const lat = Number(previewLat);
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
      const lonlat = new AMap.LngLat(lon, lat);
      const infoBase = JSON.parse(JSON.stringify(point));
      let addr = `${lon.toFixed(6)},${lat.toFixed(6)}`;
      if (this.geocoder) {
        try {
          addr = await this.getAddressByLonLat(lonlat);
        } catch (e) {
          /* 逆地理失败仍展示弹窗 */
        }
      }
      this.showInfoWindow({
        lonlat,
        infoData: {
          ...infoBase,
          gpsStatus: point.gpsStatus,
          addr,
          vno: this.getInfoPlateNo()
        }
      });
    },
    handleTracePointSeek(pathIndex, pointData) {
      if (this.trackData && this.trackData.segmentKeys) {
        const index = this.resolveTracePointIndex(pathIndex, pointData)
        const point = this.traceList[index]
        if (point) this.showPreviewPointInfoWindow(index, point.lon, point.lat)
        return
      }
      if (!this.navg) return
      const globalIdx = this.resolveTracePointIndex(pathIndex, pointData)
      if (globalIdx < 0) return
      const wasPlaying = this.playStatus === 'start'
      if (wasPlaying) {
        try { this.navg.pause() } catch (e) { /* ignore */ }
        this.pauseDistance = this.navg.getMovedDistance()
        this.playStatus = 'pause'
      }
      const savedPauseDistance = this.pauseDistance
      const savedSliderVal = this.sliderVal
      const targetIdx = Math.min(Math.max(0, Math.round(Number(globalIdx))), this.traceList.length - 1)
      this.navg.stop()
      this.navg.start()
      this.navg.pause()
      if (targetIdx === 0) {
        this.navg.moveToPoint(0)
      } else {
        this.navg.moveToPoint(targetIdx)
      }
      const previewPos = this.navg.getPosition()
      const previewLon = previewPos && previewPos.lng != null ? previewPos.lng : previewPos[0]
      const previewLat = previewPos && previewPos.lat != null ? previewPos.lat : previewPos[1]
      let previewAngle = 0
      if (typeof this.navg.getCursorAngleRadians === 'function') {
        const rad = this.navg.getCursorAngleRadians()
        previewAngle = ((-90 * Math.PI) / 180 + rad) * (180 / Math.PI)
      }
      this.restoreNavigatorToPauseDistance(savedPauseDistance)
      this.pauseDistance = savedPauseDistance
      this.sliderVal = savedSliderVal
      this.tracePointPreviewActive = !this.disablePointPreviewVehicle
      if (this.disablePointPreviewVehicle) this.clearPreviewVehicleMarker()
      else this.showPreviewVehicleMarker(previewLon, previewLat, previewAngle)
      this.pathSimplifierIns && this.pathSimplifierIns.renderLater()
      this.$emit('nav-progress', {
        ratio: this.distanceAll > 0 ? savedPauseDistance / this.distanceAll : 0,
        navPos: { lon: previewLon, lat: previewLat },
        isPlaying: false,
        previewOnly: true
      })
      this.showPreviewPointInfoWindow(targetIdx, previewLon, previewLat)
    },
    async ensureMapReady() {
      if (this.mapInstance && this.AMap) return;
      if (this.mapReadyPromise) return this.mapReadyPromise;
      this.mapReadyPromise = (async () => {
        // 与 @/utils/amapConfig 单例脚本加载一致，避免与 LeftJoinPlugin 的 loadAmapSDK 混用 jsapi-loader
        setAmapSecurityConfig()
        await loadAmapSDK(['AMap.Geocoder', 'AMap.Scale', 'AMap.GeometryUtil'])
        this.AMap = window.AMap
        const AMapUIGlobal = await loadAmapUIScript()
        await new Promise((resolve, reject) => {
          try {
            AMapUIGlobal.load(['ui/misc/PathSimplifier'], (PathSimplifier) => {
              if (!PathSimplifier) {
                reject(new Error('PathSimplifier 不可用'))
                return
              }
              this.pathSimplifierCtor = PathSimplifier
              resolve()
            })
          } catch (e) {
            reject(e)
          }
        })
        this.mapInstance = new this.AMap.Map(this.$refs.mapBox, {
          zoom: 12,
          center: [108.7, 34.3],
          resizeEnable: true,
          animateEnable: false,
          viewMode: '2D',
          mapStyle: 'amap://styles/normal',
          zooms: [3, 20],
          features: ['bg', 'point', 'road', 'building', 'boundary'],
          showLabel: true,
          // 允许截图读取 WebGL 缓冲，避免导出时只有白底。
          WebGLParams: {
            preserveDrawingBuffer: true
          }
        });
        if (typeof window !== 'undefined') {
          window.__AMAP_MAP__ = this.mapInstance;
        }
        // 比例尺控件
        const scaleControl = this.createScaleControl();
        this.mapInstance.addControl(scaleControl);
        // 信息窗/地理编码等插件
        this.initMapPlugins();
        // PathSimplifier（轨迹渲染 + 轨迹点事件）
        this.createPathSimplifier();
        // 拖拽态守卫（用户拖图时暂停自动聚焦）
        this.bindMapFollowGuards();
      })().finally(() => {
        // 若初始化失败，允许下次重试
        if (!this.mapInstance || !this.AMap) this.mapReadyPromise = null;
      });
      return this.mapReadyPromise;
    },
    normalizeLonLatToGcj(lon, lat) {
      const nlon = Number(lon);
      const nlat = Number(lat);
      if (!Number.isFinite(nlon) || !Number.isFinite(nlat)) return [0, 0];
      const useScaled = Math.max(Math.abs(nlon), Math.abs(nlat)) > 10000;
      if (useScaled) {
        const lonWgs84 = nlon / 600000.0;
        const latWgs84 = nlat / 600000.0;
        return coordtransform.wgs84togcj02(lonWgs84, latWgs84);
      }
      return [nlon, nlat];
    },
    cleanupMapTools() {
      if (this.ruler) {
        this.ruler.turnOff()
        this.ruler = null
      }
      this.isRanging = false
      if (this.tileLayer.satellite) this.tileLayer.satellite.setMap(null)
      if (this.tileLayer.roadNet) this.tileLayer.roadNet.setMap(null)
      this.showSatellite = false
      this.clearEstimateRouteOverlay()
    },
    zoomInMap() {
      if (!this.mapInstance) return
      const currentZoom = this.mapInstance.getZoom()
      const maxZoom = this.mapInstance.getZooms ? this.mapInstance.getZooms()[1] : 20
      if (currentZoom < maxZoom) this.mapInstance.setZoom(currentZoom + 1)
    },
    zoomOutMap() {
      if (!this.mapInstance) return
      const currentZoom = this.mapInstance.getZoom()
      const minZoom = this.mapInstance.getZooms ? this.mapInstance.getZooms()[0] : 3
      if (currentZoom > minZoom) this.mapInstance.setZoom(currentZoom - 1)
    },
    toggleSatelliteLayer() {
      if (!this.mapInstance) return this.showSatellite
      const AMap = this.AMap || window.AMap
      if (!AMap || !AMap.TileLayer) return this.showSatellite
      if (this.showSatellite) {
        if (this.tileLayer.satellite) this.tileLayer.satellite.setMap(null)
        if (this.tileLayer.roadNet) this.tileLayer.roadNet.setMap(null)
        this.showSatellite = false
        return this.showSatellite
      }
      if (!this.tileLayer.satellite) this.tileLayer.satellite = new AMap.TileLayer.Satellite({ zIndex: 10 })
      if (!this.tileLayer.roadNet) this.tileLayer.roadNet = new AMap.TileLayer.RoadNet({ zIndex: 11 })
      this.tileLayer.satellite.setMap(this.mapInstance)
      this.tileLayer.roadNet.setMap(this.mapInstance)
      this.showSatellite = true
      return this.showSatellite
    },
    async toggleRanging() {
      if (!this.mapInstance) return this.isRanging
      const AMap = this.AMap || window.AMap
      if (!AMap) return this.isRanging
      if (this.isRanging) {
        if (this.ruler) {
          this.ruler.turnOff()
          this.ruler = null
        }
        this.isRanging = false
        return this.isRanging
      }
      if (typeof AMap.RangingTool === 'undefined' && typeof AMap.plugin === 'function') {
        await new Promise((resolve, reject) => {
          try {
            AMap.plugin(['AMap.RangingTool'], resolve)
          } catch (e) {
            reject(e)
          }
        })
      }
      if (typeof AMap.RangingTool === 'undefined') return this.isRanging
      this.ruler = new AMap.RangingTool(this.mapInstance)
      this.ruler.turnOn()
      this.isRanging = true
      return this.isRanging
    },
    bindMapFollowGuards() {
      if (!this.mapInstance || this.mapDragStartHandler || this.mapDragEndHandler) return;
      this.mapDragStartHandler = () => {
        this.mapUserDragging = true;
      };
      this.mapDragEndHandler = () => {
        this.mapUserDragging = false;
      };
      this.mapInstance.on("dragstart", this.mapDragStartHandler);
      this.mapInstance.on("dragend", this.mapDragEndHandler);
      if (!this.mapZoomEndHandler) {
        this.mapZoomEndHandler = () => {
          refreshTrackOverlayMarkerIcons(this);
        };
        this.mapInstance.on("zoomend", this.mapZoomEndHandler);
      }
    },
    unbindMapFollowGuards() {
      if (!this.mapInstance) return;
      if (this.mapDragStartHandler) {
        this.mapInstance.off("dragstart", this.mapDragStartHandler);
      }
      if (this.mapDragEndHandler) {
        this.mapInstance.off("dragend", this.mapDragEndHandler);
      }
      if (this.mapZoomEndHandler) {
        this.mapInstance.off("zoomend", this.mapZoomEndHandler);
      }
      this.mapDragStartHandler = null;
      this.mapDragEndHandler = null;
      this.mapZoomEndHandler = null;
      this.mapUserDragging = false;
    },
    isLngLatInViewport(lon, lat) {
      if (!this.mapInstance || !Number.isFinite(Number(lon)) || !Number.isFinite(Number(lat))) return true;
      try {
        const bounds = this.mapInstance.getBounds();
        if (bounds && typeof bounds.contains === "function") {
          return bounds.contains(new this.AMap.LngLat(Number(lon), Number(lat)));
        }
      } catch (e) {
        /* ignore */
      }
      return true;
    },
    maybeFocusVehicle(lon, lat, { force = false } = {}) {
      if (!this.mapInstance) return;
      if (this.mapUserDragging) return;
      const nlon = Number(lon);
      const nlat = Number(lat);
      if (!Number.isFinite(nlon) || !Number.isFinite(nlat)) return;

      if (!force) {
        const now = Date.now();
        if (this.followFocusLastTs && now - this.followFocusLastTs < this.followFocusCooldownMs) return;
        this.followFocusLastTs = now;
        if (this.isLngLatInViewport(nlon, nlat)) return;
      }
      try {
        this.mapInstance.setCenter([nlon, nlat]);
      } catch (e) {
        /* ignore */
      }
    },
    // 供父页转发：右侧明细点击位置后，地图定位并弹出 info
    locateOnMap(payload) {
      if (!this.mapInstance || !payload) return;
      const isInvalidAddr = (val) => {
        const s = val == null ? "" : String(val).trim();
        if (!s || s === "--" || s === "-" || s === "鼠标移上获取位置..." || s === "获取位置中...") return true;
        if (isLikelyRawLonLatPairText(s)) return true;
        return false;
      };
      const refreshAddrIfNeeded = async (lonlat, infoData) => {
        if (!isInvalidAddr(infoData && infoData.addr)) return;
        try {
          const addr = await this.getAddressByLonLat(lonlat);
          if (isInvalidAddr(addr)) return;
          this.showInfoWindow({
            lonlat,
            infoData: {
              ...infoData,
              addr
            }
          });
        } catch (e) {
          // ignore geocoder fallback errors
        }
      };
      // 停车事件：优先使用“地图停车点 marker”同源坐标（parking.beginLon/beginLat），保证弹窗经纬度与图标一致
      if (payload.popupType === "parking" && Array.isArray(this.showTrackParkingDetailList) && this.showTrackParkingDetailList.length) {
        const normalizeToMs = (raw) => {
          if (raw == null || raw === "") return null;
          if (typeof raw === "number" && Number.isFinite(raw)) return raw < 1e12 ? raw * 1000 : raw;
          const s = String(raw).trim();
          if (!s) return null;
          if (/^\d+$/.test(s)) {
            const n = Number(s);
            if (!Number.isFinite(n)) return null;
            return n < 1e12 ? n * 1000 : n;
          }
          const t = new Date(s.replace(/-/g, "/")).getTime();
          return Number.isFinite(t) ? t : null;
        };
        const targetMs =
          normalizeToMs(payload.gpsTime) ||
          normalizeToMs(payload.parkingEndTime) ||
          normalizeToMs(payload.time);
        const normalizeParkingEndMs = (p) => {
          if (!p) return null;
          const raw = p.endGpsTime != null ? p.endGpsTime : p.endGtm != null ? p.endGtm : null;
          if (raw == null || raw === "") return null;
          if (typeof raw === "number" && Number.isFinite(raw)) return raw < 1e12 ? raw * 1000 : raw;
          const s = String(raw).trim();
          if (!s) return null;
          if (/^\d+$/.test(s)) {
            const n = Number(s);
            if (!Number.isFinite(n)) return null;
            return n < 1e12 ? n * 1000 : n;
          }
          const m = s.match(/^(\d{8})\/(\d{6})$/);
          if (m) {
            const y = m[1].slice(0, 4);
            const mo = m[1].slice(4, 6);
            const d = m[1].slice(6, 8);
            const hh = m[2].slice(0, 2);
            const mm = m[2].slice(2, 4);
            const ss = m[2].slice(4, 6);
            const ts = new Date(`${y}-${mo}-${d} ${hh}:${mm}:${ss}`).getTime();
            return Number.isFinite(ts) ? ts : null;
          }
          return null;
        };
        const withinMs = 60 * 1000; // 允许 1 分钟误差（接口端时间格式/取整差异）
        let matched = null;
        if (targetMs != null) {
          matched = this.showTrackParkingDetailList.find((p) => {
            const endMs = normalizeParkingEndMs(p);
            if (endMs == null) return false;
            return Math.abs(endMs - targetMs) <= withinMs;
          });
        }
        if (!matched && payload.parkingEndTime) {
          const endStr = String(payload.parkingEndTime).trim();
          matched = this.showTrackParkingDetailList.find((p) => p && p.endGtm != null && String(p.endGtm).trim() === endStr);
        }
        if (matched && matched.beginLon != null && matched.beginLat != null) {
          const endMs = normalizeParkingEndMs(matched);
          let pointIndex = -1;
          if (endMs != null) {
            pointIndex = this.traceList.findIndex((point) => {
              if (!point) return false;
              const ptMs = point.gpsTime != null && point.gpsTime !== '' ? Number(point.gpsTime) : NaN;
              return Number.isFinite(ptMs) && ptMs === endMs;
            });
          }
          if (pointIndex < 0 && matched.endGtm) {
            pointIndex = this.traceList.findIndex(
              (point) => point && point.gtm && String(point.gtm).trim() === String(matched.endGtm).trim()
            );
          }
          this.handleParkingPointPreview(matched, pointIndex);
          return;
        }
      }
      const isGcj =
        payload.isGcj === true ||
        payload.coordType === "gcj02" ||
        payload.coordType === "GCJ02";
      const [lon, lat] = isGcj
        ? [Number(payload.lon), Number(payload.lat)]
        : this.normalizeLonLatToGcj(payload.lon, payload.lat);
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
      const lonlat = [lon, lat];
      const speed = this.qualityMode
        ? (payload.speed != null && payload.speed !== '' && Number.isFinite(Number(payload.speed)) && Number(payload.speed) >= 0 ? Number(payload.speed) : null)
        : Number(payload.speed != null ? payload.speed : (payload.spd != null ? payload.spd : payload.hgt));
      const parsedTime = this.qualityMode && payload.gpsTime != null ? Number(payload.gpsTime) : payload.time != null ? new Date(String(payload.time).replace(/-/g, "/")).getTime() : NaN;
      const gpsTime = Number.isFinite(parsedTime) ? parsedTime : Number(payload.gpsTime);
      const infoData = {
        lon,
        lat,
        spd: this.qualityMode ? speed : Number.isFinite(speed) ? speed : 0,
        agl: payload.agl != null && payload.agl !== "" ? payload.agl : payload.angle,
        gpsTime: Number.isFinite(gpsTime) ? gpsTime : null,
        hgt: payload.hgt != null && payload.hgt !== "" ? payload.hgt : 0,
        gpsStatus: payload.gpsStatus,
        popupType: payload.popupType || "normal",
        parkingStartTime: payload.parkingStartTime || "--",
        parkingEndTime: payload.parkingEndTime || "--",
        parkingDuration: payload.parkingDuration || "--",
        addr: payload.location || payload.addr || "--",
        vno: payload.plateNumber || this.getInfoPlateNo()
      };
      this.mapInstance.setCenter(lonlat);
      this.showInfoWindow({ lonlat, infoData });
      refreshAddrIfNeeded(lonlat, infoData);
    },
    // 从路由 query 读取参数，初始化 formItem
    initForm() {
      const query = this.$route.query;
      this.formItem.vno = query.vno || "";
      this.formItem.vco = query.vco || "";
      this.formItem.startTime = query.startTime || "";
      this.formItem.endTime = query.endTime || "";
    },
    // 展示主标识：优先父组件传入（如 type=6 运单号/查询车牌），其次 query，再左侧表单/父页，再次 scoped localStorage
    getInfoPlateNo() {
      const fromProp = this.displayPrimaryId != null ? String(this.displayPrimaryId).trim() : "";
      if (fromProp) return fromProp;
      const fromQuery = this.formItem && this.formItem.vno != null ? String(this.formItem.vno).trim() : "";
      if (fromQuery) return fromQuery;
      const parent = this.$parent;
      if (parent && parent.tripPlateFromQuery != null && String(parent.tripPlateFromQuery).trim()) {
        return String(parent.tripPlateFromQuery).trim();
      }
      const leftForm =
        parent &&
        parent.$refs &&
        parent.$refs.leftJoin &&
        parent.$refs.leftJoin.searchForm
          ? parent.$refs.leftJoin.searchForm
          : null;
      if (leftForm) {
        const fromLeft =
          leftForm.licensePlate != null
            ? String(leftForm.licensePlate).trim()
            : leftForm.vclN != null
            ? String(leftForm.vclN).trim()
            : leftForm.vno != null
            ? String(leftForm.vno).trim()
            : "";
        if (fromLeft) return fromLeft;
      }
      return "--";
    },
    // 停车时长阈值（分钟）：优先父组件传入，其次读取父页 LeftJoin，再次读取路由 query
    resolveParkMinsThreshold() {
      if (Number.isFinite(Number(this.parkMinsThreshold))) {
        return Math.max(0, Number(this.parkMinsThreshold));
      }
      const leftJoin =
        this.$parent &&
        this.$parent.$refs &&
        this.$parent.$refs.leftJoin &&
        this.$parent.$refs.leftJoin.searchForm
          ? this.$parent.$refs.leftJoin.searchForm
          : null;
      if (leftJoin && Number.isFinite(Number(leftJoin.parkMins))) {
        return Math.max(0, Number(leftJoin.parkMins));
      }
      const q = this.$route && this.$route.query ? this.$route.query : {};
      if (Number.isFinite(Number(q.parkMins))) {
        return Math.max(0, Number(q.parkMins));
      }
      return 0;
    },

    // 打开/更新信息窗（轨迹点信息展示入口）
    showInfoWindow(data) {
      const { lonlat, infoData } = data;
      if (infoData && infoData.popupType !== 'parking') {
        this._parkingInfoRestore = null
      }
      this._lastInfoPayload = data
      this.infoWindow.setContent(this.createInfoWindowContent(infoData));
      if (this.infoWindow.getIsOpen()) {
        this.infoWindow.setPosition(lonlat);
      } else {
        this.infoWindow.open(this.mapInstance, lonlat);
      }
      this.bindInfoWindowCloseButton();
    },
    isInfoWindowOpen() {
      return !!(
        this.infoWindow &&
        typeof this.infoWindow.getIsOpen === 'function' &&
        this.infoWindow.getIsOpen()
      )
    },
    closeInfoWindowQuiet() {
      try {
        if (this.infoWindow && typeof this.infoWindow.close === 'function') this.infoWindow.close()
      } catch (e) { /* ignore */ }
    },
    // 与弹窗同源：统一格式化轨迹点时间
    formatPopupUpdateTime(pointData) {
      const t = Number(pointData && pointData.gpsTime);
      if (!Number.isFinite(t)) return "--";
      if (this.qualityMode) return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(new Date(t));
      return this.$dayjs(t).format("YYYY-MM-DD HH:mm:ss");
    },
    // 动态同步 PathSimplifier 路线名中的“更新时间”
    syncTrackUpdateTime(pointData) {
      if (!this.pathSimplifierIns) return;
      const timeText = this.formatPopupUpdateTime(pointData);
      const nextTitle = `更新时间：${timeText}`;
      if (this.currentTrackTitle === nextTitle) return;
      const pathData =
        typeof this.pathSimplifierIns.getPathData === "function"
          ? this.pathSimplifierIns.getPathData(this.getNavigatorPathIndex())
          : null;
      if (!pathData) return;
      pathData.name = nextTitle;
      this.currentTrackTitle = nextTitle;
      this.pathSimplifierIns.renderLater();
    },
    // 将 PathSimplifier 点事件参数解析为轨迹点对象（兼容不同事件参数形态）
    resolveTracePointIndex(pathIndex, pointArg) {
      return resolveTracePointIndexFromEvent(this.pathSimplifierIns, pathIndex, pointArg)
    },
    resolveTracePointFromEventArg(pathIndex, pointArg) {
      const idx = this.resolveTracePointIndex(pathIndex, pointArg)
      if (idx < 0 || idx >= this.traceList.length) return null
      return this.traceList[idx]
    },
    // 给 hover 用：从当前索引向前找最近有效点（gtm 存在），避免无效点时标题变成 "--"
    resolveHoverUpdatePoint(pathIndex, pointArg) {
      const idx = this.resolveTracePointIndex(pathIndex, pointArg)
      for (let i = idx; i >= 0; i--) {
        if (this.traceList[i] && this.traceList[i].gtm) {
          return { point: this.traceList[i], index: i };
        }
      }
      return null;
    },
    // 悬停移出后恢复“更新时间”：优先当前导航点，其次首点
    getRestoreUpdateTimePoint() {
      if (this.navg && this.traceList.length) {
        const idx = this.getPreviousNormalTraceIndex();
        return this.traceList[idx] || this.traceList[0] || null;
      }
      return this.traceList[0] || null;
    },

    // 组装信息窗 HTML（PathSimplifier 的轨迹点/导航位置更新会调用）
    createInfoWindowContent(pointData) {
      const escapeHtml = value => String(value == null ? '' : value).replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
      const gpsTimeStr = escapeHtml(this.formatPopupUpdateTime(pointData));
      const popupType = pointData && pointData.popupType ? String(pointData.popupType) : "normal";
      const statusText = pointData.gpsTime ? getGpsStatusConfig(pointData.gpsStatus).str : "--";
      let headerStatus = statusText === "行驶" ? "行驶" : "停止";
      let statusClass = headerStatus === "行驶" ? "is-moving" : "is-stop";
      const unknownSpeed = pointData.spd == null || String(pointData.spd).trim() === "" || !Number.isFinite(Number(pointData.spd)) || Number(pointData.spd) < 0;
      if (this.qualityMode) {
        headerStatus = "速度未知";
        statusClass = "";
        if (!unknownSpeed) {
          const moving = Number(pointData.spd) > 0;
          headerStatus = moving ? "行驶" : "停车";
          statusClass = moving ? "is-moving" : "is-stop";
        }
      }
      const locationLabel = this.qualityMode ? "位置" : "预警位置";
      const speedText = escapeHtml(this.qualityMode && unknownSpeed ? "未知" : (pointData.spd || "0") + "km/h");
      if (popupType === "start") {
        headerStatus = "起点";
        statusClass = "is-start";
      } else if (popupType === "end") {
        headerStatus = "终点";
        statusClass = "is-end";
      } else if (popupType === "originSearch") {
        headerStatus = "装货地";
        statusClass = "is-start";
      } else if (popupType === "destSearch") {
        headerStatus = "卸货地";
        statusClass = "is-end";
      } else if (popupType === "parking") {
        headerStatus = "停车事件";
        statusClass = "is-parking";
      }
      const directionText = escapeHtml(this.getInfoDirectionDesc(pointData));
      const showParkingRows = popupType === "parking";
      const showDirectionRow = popupType !== "parking" && popupType !== "originSearch" && popupType !== "destSearch";
      const isCargoPoint = popupType === "originSearch" || popupType === "destSearch";
      const parkingStartTime = escapeHtml(pointData && pointData.parkingStartTime ? pointData.parkingStartTime : "--");
      const parkingEndTime = escapeHtml(pointData && pointData.parkingEndTime ? pointData.parkingEndTime : "--");
      const parkingDuration = escapeHtml(pointData && pointData.parkingDuration ? pointData.parkingDuration : "--");
      const navTargetName = pointData && pointData.addr ? String(pointData.addr).trim() : "目的地";
      const navUrl =
        Number.isFinite(Number(pointData.lon)) && Number.isFinite(Number(pointData.lat))
          ? `https://uri.amap.com/navigation?to=${`${Number(pointData.lon).toFixed(6)},${Number(pointData.lat).toFixed(6)},${encodeURIComponent(navTargetName)}`}`
          : "";
      if (isCargoPoint) {
        return `
          <div class="infoWidowBox">
            <div class="titleBox">
              <div class="vehInfo">
                <span class="vehNumber">${escapeHtml(pointData.vno)}</span>
              </div>
              <span class="vehStatus ${statusClass}">${headerStatus}</span>
              <span class="infoCloseBtn" role="button" aria-label="关闭">×</span>
            </div>
            <div class="markerContent">
              <p class="markerLine">
                <span class="markerS1">${locationLabel}：</span>
                <span class="markerS2 markerAddr">
                ${escapeHtml(pointData.addr || "")}
                ${navUrl ? `<a class="markerNavBtn" href="${escapeHtml(navUrl)}" target="_blank" rel="noopener noreferrer">导航</a>` : ""}
              </span>
              </p>
            </div>
          </div>
        `;
      }
      if (showParkingRows) {
        return `
          <div class="infoWidowBox">
            <div class="titleBox">
              <div class="vehInfo">
                <span class="vehNumber">${escapeHtml(pointData.vno)}</span>
              </div>
              <span class="vehStatus ${statusClass}">${headerStatus}</span>
              <span class="infoCloseBtn" role="button" aria-label="关闭">×</span>
            </div>
            <div class="markerContent">
              <p class="markerLine">
                <span class="markerS1">${locationLabel}：</span>
                <span class="markerS2 markerAddr">
                ${escapeHtml(pointData.addr || "")}
                ${navUrl ? `<a class="markerNavBtn" href="${escapeHtml(navUrl)}" target="_blank" rel="noopener noreferrer">导航</a>` : ""}
              </span>
              </p>
              <p class="markerLine">
                <span class="markerS1">开始时间：</span>
                <span class="markerS2">${parkingStartTime}</span>
              </p>
              <p class="markerLine">
                <span class="markerS1">结束时间：</span>
                <span class="markerS2">${parkingEndTime}</span>
              </p>
              <p class="markerLine">
                <span class="markerS1">停车时长：</span>
                <span class="markerS2">${parkingDuration}</span>
              </p>
            </div>
          </div>
        `;
      }
      return `
        <div class="infoWidowBox">
          <div class="titleBox">
            <div class="vehInfo">
              <span class="vehNumber">${escapeHtml(pointData.vno)}</span>
            </div>
            <span class="vehStatus ${statusClass}">${headerStatus}</span>
            <span class="infoCloseBtn" role="button" aria-label="关闭">×</span>
            <div class="speedBox">
              ${speedText}
            </div>
          </div>
          <div class="markerContent">
            <p class="markerLine">
              <span class="markerS1">速度：</span>
              <span class="markerS2">${speedText}</span>
            </p>
            ${showDirectionRow ? `
            <p class="markerLine">
              <span class="markerS1">方向：</span>
              <span class="markerS2">${directionText}</span>
            </p>` : ""}
            ${this.vehicleMonitorInfo ? `
            <p class="markerLine">
              <span class="markerS1">剩余里程：</span>
              <span class="markerS2">${escapeHtml(this.vehicleMonitorInfo.remainingDistance)}</span>
            </p>
            <p class="markerLine">
              <span class="markerS1">预计到达：</span>
              <span class="markerS2">${escapeHtml(this.vehicleMonitorInfo.estimatedArriveTime)}</span>
            </p>` : ""}
            <p class="markerLine">
              <span class="markerS1">${locationLabel}：</span>
              <span class="markerS2 markerAddr">
                ${escapeHtml(pointData.addr || "")}
                ${navUrl ? `<a class="markerNavBtn" href="${escapeHtml(navUrl)}" target="_blank" rel="noopener noreferrer">导航</a>` : ""}
              </span>
            </p>
            ${showParkingRows ? `
            <p class="markerLine">
              <span class="markerS1">开始时间：</span>
              <span class="markerS2">${parkingStartTime}</span>
            </p>
            <p class="markerLine">
              <span class="markerS1">结束时间：</span>
              <span class="markerS2">${parkingEndTime}</span>
            </p>
            <p class="markerLine">
              <span class="markerS1">停车时长：</span>
              <span class="markerS2">${parkingDuration}</span>
            </p>` : ""}
            <p class="markerLine">
              <span class="markerS1">更新时间：</span>
              <span class="markerS2">${gpsTimeStr || "--"}</span>
            </p>
          </div>
        </div>
      `;
    },
    bindInfoWindowCloseButton() {
      setTimeout(() => {
        const closeBtn = document.querySelector(".infoWidowBox .infoCloseBtn");
        if (closeBtn) {
          closeBtn.onclick = (e) => {
            e.stopPropagation();
            this.infoWindow && this.infoWindow.close();
          };
        }
      }, 0);
    },
    // 与轨迹明细一致：角度或已有方位字面统一映射为东/南/西/北
    directionToCardinal4(v) {
      if (v === "北" || v === "东" || v === "南" || v === "西") return v;
      const n = Number(v);
      const d = Number.isFinite(n) ? ((n % 360) + 360) % 360 : 0;
      if (d >= 315 || d < 45) return "北";
      if (d < 135) return "东";
      if (d < 225) return "南";
      return "西";
    },
    getDirectionDesc(a) {
      if (a == "北" || a == "东" || a == "南" || a == "西" || a == "东北" || a == "东南" || a == "西南" || a == "西北") return a;
      let deg = Number(a);
      if (!Number.isFinite(deg)) return "--";
      if (deg < 0) deg += 360;
      if (deg > 360) deg -= 360;
      if (deg <= 22.5 || deg > 337.5) return "北";
      if (deg > 22.5 && deg <= 67.5) return "东北";
      if (deg > 67.5 && deg <= 112.5) return "东";
      if (deg > 112.5 && deg <= 157.5) return "东南";
      if (deg > 157.5 && deg <= 202.5) return "南";
      if (deg > 202.5 && deg <= 247.5) return "西南";
      if (deg > 247.5 && deg <= 292.5) return "西";
      if (deg > 292.5 && deg <= 337.5) return "西北";
      return "--";
    },
    calcAngle(a, b) {
      if (!a || !b) return null;
      const aLon = Number(a.longitude);
      const aLat = Number(a.latitude);
      const bLon = Number(b.longitude);
      const bLat = Number(b.latitude);
      if (![aLon, aLat, bLon, bLat].every(Number.isFinite)) return null;
      let dRotateAngle = Math.atan2(
        Math.abs(aLon - bLon),
        Math.abs(aLat - bLat)
      );
      if (bLon >= aLon) {
        if (!(bLat >= aLat)) {
          dRotateAngle = Math.PI - dRotateAngle;
        }
      } else if (bLat >= aLat) {
        dRotateAngle = 2 * Math.PI - dRotateAngle;
      } else {
        dRotateAngle = Math.PI + dRotateAngle;
      }
      return (dRotateAngle * 180) / Math.PI;
    },
    getInfoDirectionDesc(pointData) {
      const idx = Number(pointData && pointData.pointIndex);
      if (Number.isFinite(idx) && this.traceList && this.traceList.length) {
        const cur = this.traceList[idx];
        const next = idx < this.traceList.length - 1 ? this.traceList[idx + 1] : null;
        const prev = idx > 0 ? this.traceList[idx - 1] : null;
        const fromPoint = next ? cur : prev;
        const toPoint = next || cur;
        const deg = this.calcAngle(
          fromPoint ? { longitude: fromPoint.lon, latitude: fromPoint.lat } : null,
          toPoint ? { longitude: toPoint.lon, latitude: toPoint.lat } : null
        );
        if (Number.isFinite(deg)) return this.getDirectionDesc(deg);
      }
      const raw =
        pointData && pointData.agl != null && pointData.agl !== ""
          ? pointData.agl
          : pointData && pointData.angle != null
            ? pointData.angle
            : null;
      if (raw == null || raw === "") return "--";
      return this.getDirectionDesc(raw);
    },
    // 停车秒数转展示文案（优先分钟，超过 60 分钟按小时+分钟）
    formatParkingDuration(parkSeconds) {
      const sec = Number(parkSeconds);
      if (!Number.isFinite(sec) || sec <= 0) return "0分钟";
      const mins = Math.max(1, Math.floor(sec / 60));
      if (mins < 60) return `${mins}分钟`;
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return m ? `${h}小时${m}分钟` : `${h}小时`;
    },
    formatParkingDetailTime(ts) {
      if (typeof ts === "string" && ts.includes("/")) {
        const [ymd, hms] = ts.split("/");
        if (ymd && ymd.length === 8 && hms && hms.length === 6) {
          return `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)} ${hms.slice(0, 2)}:${hms.slice(2, 4)}:${hms.slice(4, 6)}`;
        }
      }
      const n = Number(ts);
      if (!Number.isFinite(n)) return "--";
      return this.$dayjs(n).format("YYYY-MM-DD HH:mm:ss");
    },

    // 重置轨迹播放/进度相关状态（此页不提供播放 UI，但内部事件仍依赖这些字段）
    resetPlayStatus() {
      this.isCanPlay = false;
      this.distanceAll = 0;
      this.sliderVal = 0;
      this.barStartTime = "00:00";
      this.barEndTimes = 0;
      this.barEndTime = "00:00";
      this.playStatus = "stop";
      this.speedName = "倍速";
      this.traceList = [];
      this.markerStopList = [];
      this.parkingMarkerRecords = [];
      this.showTrackParkingDetailList = [];
      this.hidePathLineOnly = false
      this.parkingMarkersHidden = false
      this._pathLineBk = null
      this._lastInfoPayload = null
      this._parkingInfoRestore = null
      this.tripCardSyncedStartLine = '';
      this.tripCardSyncedEndLine = '';
      this.emitTripCardSyncedAddressLines();
    },

    // 清空地图覆盖物与已绘制轨迹（用于切换车辆/重新加载轨迹）
    clearMap() {
      this.clearIndependentPositionMarkers()
      this.clearJointPlayback()
      this.mapInstance && this.mapInstance.clearMap();
      this.pathSimplifierIns && this.pathSimplifierIns.setData([{ name: "路线", path: [] }]);
      this.currentTrackPath = [];
      this.currentTrackTitle = "";
      this.hoverPointIndex = -1;
      this.hoverUpdatePointIndex = -1;
      this.suppressHoverOutUntil = 0;
      this.clearEstimateRouteOverlay();
    },

    hasRenderableTrackData() {
      const externalData = this.trackData;
      return !!(
        externalData &&
        Array.isArray(externalData.traceList) &&
        externalData.traceList.length
      );
    },
    trackDataSignature(data) {
      if (!data || !Array.isArray(data.traceList) || !data.traceList.length) return '';
      if (data.segmentKeys) return `${data.traceList.join('|')}#${data.segmentKeys.join('|')}`
      const list = data.traceList;
      const first = list[0] || {};
      const last = list[list.length - 1] || {};
      return `${list.length}|${first.lon},${first.lat}|${last.lon},${last.lat}`;
    },
    parkingEventKey(parking) {
      return JSON.stringify([
        normalizeParkingEventTimeMs({ startTime: parking.beginGpsTime == null ? parking.startGtm : parking.beginGpsTime }),
        normalizeParkingEventTimeMs(parking), Number(parking.beginLon), Number(parking.beginLat)
      ])
    },
    syncExternalParkingData() {
      if (!this.mapInstance || !this.AMap || !this.trackData) return
      const parkInfo = this.trackData.parkInfo || {}
      const thresholdSec = this.resolveParkMinsThreshold() * 60
      this.showTrackParkingDetailList = (parkInfo.trackParkingDetailList || []).filter(item => Number(item && item.parkTime) >= thresholdSec)
      const containsParking = payload => {
        const info = payload && payload.infoData
        if (!info || info.popupType !== 'parking') return true
        const key = this.parkingEventKey({ beginGpsTime: info.parkingBeginGpsTime == null ? info.parkingStartTime : info.parkingBeginGpsTime, endGpsTime: info.gpsTime == null ? info.parkingEndTime : info.gpsTime, beginLon: info.lon, beginLat: info.lat })
        return this.showTrackParkingDetailList.some(item => this.parkingEventKey(item) === key)
      }
      if (!containsParking(this._parkingInfoRestore)) this._parkingInfoRestore = null
      if (!containsParking(this._lastInfoPayload)) this.closeInfoWindowQuiet()
      this.renderParkingMarkers()
    },
    // 轨迹加载入口（单一数据源：trajectoryData-track.js）
    async handleSearch() {
      await this.ensureMapReady();
      this.clearMap();
      this.initStartEndMarkers();
      this.resetPlayStatus();
      await this.loadTrackData();
    },
    // 按 index.vue 原逻辑：播放（首次从头，暂停后从 pauseDistance 继续；播完再播从选区/轨迹起点重播）
    handlePlay() {
      if (!this.isCanPlay) {
        this.$message.error("暂无可播放轨迹");
        return;
      }
      if (!this.navg) return;
      this.tracePointPreviewActive = false;
      this.clearPreviewVehicleMarker();
      const navTotal = this.getNavigatorDistanceAll();
      let resumeFrom = Math.max(0, Number(this.pauseDistance) || 0);
      try {
        const live = Number(this.navg.getMovedDistance());
        if (Number.isFinite(live) && live >= 0) {
          resumeFrom = live;
          this.pauseDistance = live;
        }
      } catch (e) {
        /* ignore */
      }
      const atEnd = navTotal > 0 && resumeFrom >= navTotal - 1e-3;
      let naviStatus = '';
      try {
        naviStatus = typeof this.navg.getNaviStatus === 'function' ? String(this.navg.getNaviStatus() || '') : '';
      } catch (e) {
        naviStatus = '';
      }
      if (this.playStatus === "stop" || atEnd) {
        this.pauseDistance = 0;
        this.navg.stop();
        this.navg.start();
        this.playStatus = "start";
      } else if (this.playStatus === "pause" || naviStatus === 'pause') {
        // seek 后已是 pause 落点：只 resume。若再 start+pause，pause 回调可能把导航器 stop 掉导致 resume 无效
        if (naviStatus === 'pause') {
          this.navg.resume();
        } else {
          // 状态机不在 pause：安全重建定位后再 resume（屏蔽 pause 结束误判）
          this._pathSeeking = true;
          try {
            this.navg.stop();
            this.navg.start();
            this.navg.pause();
            if (resumeFrom > 0) {
              this.navg.moveByDistance(resumeFrom);
            }
          } finally {
            this._pathSeeking = false;
          }
          this.navg.resume();
        }
        this.playStatus = "start";
      } else {
        this.$message.error("未知状态播放无效");
      }
      const pos = this.navg.getPosition ? this.navg.getPosition() : null;
      if (pos) {
        this.maybeFocusVehicle(pos.lng != null ? pos.lng : pos[0], pos.lat != null ? pos.lat : pos[1], { force: true });
      }
    },
    // 按 index.vue 原逻辑：暂停并记录暂停距离
    handlePause() {
      if (!this.navg) return
      const wasPlaying = this.playStatus === 'start'
      try {
        this.navg.pause()
      } catch (e) {
        /* ignore */
      }
      try {
        this.pauseDistance = this.navg.getMovedDistance()
      } catch (e) {
        /* ignore */
      }
      if (wasPlaying) {
        this.playStatus = 'pause'
        this.setGeocoderAddress()
      }
    },
    // 按 index.vue 原逻辑：拖动进度条后切换到对应距离并停在 pause 态
    handleSliderChange(opts = {}) {
      const preview = opts.preview === true;
      this.tracePointPreviewActive = false;
      if (!preview) {
        this.clearPreviewVehicleMarker();
      }
      const fullRatio = this.sliderVal / 100;
      const clipLocalRatio = this.mapFullRatioToClipLocal(fullRatio);
      const navTotal = this.getNavigatorDistanceAll();
      const targetDistance = navTotal * clipLocalRatio;
      this.pauseDistance = targetDistance;
      // preview 时屏蔽 move/pause 的 nav-progress，并保护 pauseDistance 不被 pause 回调清零
      this._suppressNavProgressEmit = preview === true;
      this._pathSeeking = true;
      this.navg.stop();
      this.navg.start();
      this.navg.pause();
      this.playStatus = "pause";
      targetDistance === 0
        ? this.navg.moveToPoint(0)
        : this.navg.moveByDistance(targetDistance);
      // 以实际落点为准（理论距离与导航器落点常不一致）
      try {
        const live = Number(this.navg.getMovedDistance());
        this.pauseDistance = Number.isFinite(live) && live >= 0 ? live : targetDistance;
      } catch (e) {
        this.pauseDistance = targetDistance;
      }
      this.playStatus = "pause";
      this.pathSimplifierIns.renderLater();
      this._pathSeeking = false;
      this._suppressNavProgressEmit = false;
      if (preview) return;
      this.setGeocoderAddress();
      const pos = this.navg.getPosition();
      this.maybeFocusVehicle(pos && (pos.lng != null ? pos.lng : pos[0]), pos && (pos.lat != null ? pos.lat : pos[1]), { force: true });
      this.emitNavProgressFromNavigator();
    },
    emitNavProgressFromNavigator() {
      if (!this.navg) return;
      const movedDistance = this.navg.getMovedDistance();
      const navTotal = this.getNavigatorDistanceAll();
      const fullRatio = this.mapClipLocalToFullRatio(navTotal > 0 ? movedDistance / navTotal : 0);
      this.sliderVal = fullRatio * 100;
      const currentPos = this.navg.getPosition();
      this.$emit('nav-progress', {
        ratio: fullRatio,
        navPos: {
          lon: currentPos && currentPos.lng != null ? currentPos.lng : currentPos[0],
          lat: currentPos && currentPos.lat != null ? currentPos.lat : currentPos[1]
        }
      });
    },

    // 读取并解析轨迹数据（Demo：从 trackData0~3 中选择）
    async loadTrackData() {
      if (!this.hideSearchEndpointMarkers) {
        await this.resolveSearchEndpointsForMap();
        this.renderSearchEndpointMarkers();
      } else {
        this.clearSearchEndpointMarkers();
      }
      const externalData = this.trackData
      const hasExternalData = !!(
        externalData &&
        Array.isArray(externalData.traceList) &&
        externalData.traceList.length
      )
      const trackData = hasExternalData ? externalData : (this.useDefaultData ? trajectoryTrackData : null)
      if (trackData) {
        // 解析轨迹点（将原始 traceList 标准化为可渲染结构）
        this.traceList = parseTracePoints(trackData.traceList);
        if (trackData.segmentKeys) this.traceList.forEach((point, index) => { point.segmentKey = trackData.segmentKeys[index] })
        if (this.qualityMode && trackData.qualityPoints) this.traceList.forEach((point, index) => {
          const source = trackData.qualityPoints[index]
          point.pointKey = source.pointKey
          point.spd = source.speed
          point.gpsTime = source.locationTime
          point.addr = source.address || ''
        })
        // 停车事件（用于在地图上打点，并绑定点击事件）
        const rawParkingList = trackData.parkInfo.trackParkingDetailList || [];
        const thresholdSec = this.resolveParkMinsThreshold() * 60;
        const parkingList = rawParkingList.filter((p) => Number(p && p.parkTime) >= thresholdSec);
        if (parkingList.length) {
          this.showTrackParkingDetailList = parkingList;
          this.renderParkingMarkers();
        }
        // 查询加载后关闭可能残留的 InfoWindow，不默认弹出
        this.closeInfoWindowQuiet();
        // 绘制轨迹线（并初始化导航器、起终点 marker 等）
        if (trackData.segmentKeys) {
          this.renderJointTrackPath()
          if (this.qualityMode && this.qualityRestoreTime != null && this.traceList.length > 1) {
            const start = this.traceList[0].gpsTime
            const span = this.traceList[this.traceList.length - 1].gpsTime - start
            this.jointPlayRatio = span > 0 ? Math.max(0, Math.min(1, (this.qualityRestoreTime - start) / span)) : 0
            this.updateJointPlaybackPosition()
          }
        }
        else this.renderTrackPath(this.traceList);
      } else {
        this.tripCardSyncedStartLine = '';
        this.tripCardSyncedEndLine = '';
        this.emitTripCardSyncedAddressLines();
      }
      this.syncIndependentPositionMarkers()
    },
    parseLngLatString(str) {
      if (str == null || typeof str !== "string") return null;
      const parts = String(str).trim().split(/[,，\s]+/).map((s) => s.trim()).filter(Boolean);
      if (parts.length < 2) return null;
      const lng = Number(parts[0]);
      const lat = Number(parts[1]);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
      if (Math.abs(lng) > 180 || Math.abs(lat) > 90) return null;
      return [lng, lat];
    },
    buildAddressFromCascaderCodes(codes) {
      if (!Array.isArray(codes) || !codes.length) return "";
      return codes.map((code) => codeToText[code] || "").join("");
    },
    geocodeAddressToLngLat(address) {
      return new Promise((resolve) => {
        if (!address || !this.geocoder || typeof this.geocoder.getLocation !== "function") {
          resolve(null);
          return;
        }
        this.geocoder.getLocation(address, (status, result) => {
          if (status === "complete" && result && result.geocodes && result.geocodes.length > 0) {
            const location = result.geocodes[0].location;
            if (location && typeof location.getLng === "function" && typeof location.getLat === "function") {
              resolve([location.getLng(), location.getLat()]);
              return;
            }
          }
          resolve(null);
        });
      });
    },
    geocodeAddressDetail(address) {
      return new Promise((resolve) => {
        const raw = address != null ? String(address).trim() : "";
        if (!raw || !this.geocoder || typeof this.geocoder.getLocation !== "function") {
          resolve(null);
          return;
        }
        this.geocoder.getLocation(raw, (status, result) => {
          if (status === "complete" && result && Array.isArray(result.geocodes) && result.geocodes.length > 0) {
            const first = result.geocodes[0];
            const location = first && first.location;
            const lonlat =
              location && typeof location.getLng === "function" && typeof location.getLat === "function"
                ? [location.getLng(), location.getLat()]
                : null;
            resolve({
              lonlat,
              formattedAddress:
                first && first.formattedAddress != null ? String(first.formattedAddress).trim() : ""
            });
            return;
          }
          resolve(null);
        });
      });
    },
    async resolveAreaDisplayText(areaInput, lonlat) {
      const raw = areaInput != null ? String(areaInput).trim() : "";
      if (!raw) return "";
      if (/^\d{6}$/.test(raw)) {
        const text = this.formatAreaCodeFullText(raw);
        if (text) return text;
      }
      // 如果输入本身就是“区/县/市”名称，优先用行政区字典补全（比 Geocoder 更稳定）
      const idx = getAreaNameToCodeIndex();
      const matchedCode = idx.get(raw);
      if (matchedCode && /^\d{6}$/.test(String(matchedCode))) {
        const text = this.formatAreaCodeFullText(String(matchedCode));
        if (text) return text;
      }
      const geocodeDetail = await this.geocodeAddressDetail(raw);
      if (geocodeDetail && geocodeDetail.formattedAddress) {
        return geocodeDetail.formattedAddress;
      }
      if (Array.isArray(lonlat) && lonlat.length >= 2) {
        try {
          const addr = await this.getAddressByLonLat(lonlat);
          if (addr) return addr;
        } catch (e) {
          /* ignore */
        }
      }
      return raw;
    },
    resolveAreaCenterByCode(areaCode) {
      return new Promise((resolve) => {
        const code = areaCode != null ? String(areaCode).trim() : "";
        if (!code || !this.AMap || typeof this.AMap.plugin !== "function") {
          resolve(null);
          return;
        }
        this.AMap.plugin(["AMap.DistrictSearch"], () => {
          try {
            const districtSearch = new this.AMap.DistrictSearch({
              subdistrict: 0,
              extensions: "base"
            });
            districtSearch.search(code, (status, result) => {
              if (status === "complete" && result && Array.isArray(result.districtList) && result.districtList.length) {
                const center = result.districtList[0].center;
                if (center && typeof center.getLng === "function" && typeof center.getLat === "function") {
                  resolve([center.getLng(), center.getLat()]);
                  return;
                }
              }
              resolve(null);
            });
          } catch (e) {
            resolve(null);
          }
        });
      });
    },
    clearSearchEndpointMarkers() {
      if (this.searchOriginMarker) {
        this.searchOriginMarker.setMap(null)
        this.searchOriginMarker = null
      }
      if (this.searchDestMarker) {
        this.searchDestMarker.setMap(null)
        this.searchDestMarker = null
      }
      this.lastSearchOriginGcj = null
      this.lastSearchDestGcj = null
      this.searchOriginText = ''
      this.searchDestText = ''
    },
    async resolveSearchEndpointsForMap() {
      if (this.hideSearchEndpointMarkers) {
        this.clearSearchEndpointMarkers()
        return
      }
      this.lastSearchOriginGcj = null;
      this.lastSearchDestGcj = null;
      this.searchOriginText = ''
      this.searchDestText = ''
      const areaOverride = this.searchAreaCodeOverride && typeof this.searchAreaCodeOverride === 'object'
        ? this.searchAreaCodeOverride
        : null
      const overrideStartCode =
        areaOverride && areaOverride.startAreaCode != null ? String(areaOverride.startAreaCode).trim() : ''
      const overrideEndCode =
        areaOverride && areaOverride.endAreaCode != null ? String(areaOverride.endAreaCode).trim() : ''
      if (overrideStartCode) {
        this.lastSearchOriginGcj = /^\d{6}$/.test(overrideStartCode)
          ? await this.resolveAreaCenterByCode(overrideStartCode)
          : await this.geocodeAddressToLngLat(overrideStartCode)
        this.searchOriginText = await this.resolveAreaDisplayText(overrideStartCode, this.lastSearchOriginGcj)
      }
      if (overrideEndCode) {
        this.lastSearchDestGcj = /^\d{6}$/.test(overrideEndCode)
          ? await this.resolveAreaCenterByCode(overrideEndCode)
          : await this.geocodeAddressToLngLat(overrideEndCode)
        this.searchDestText = await this.resolveAreaDisplayText(overrideEndCode, this.lastSearchDestGcj)
      }
      const leftJoin =
        this.$parent &&
        this.$parent.$refs &&
        this.$parent.$refs.leftJoin
          ? this.$parent.$refs.leftJoin
          : null;
      if (!leftJoin || !leftJoin.searchForm) return;
      const form = leftJoin.searchForm;
      let originGcj = this.lastSearchOriginGcj || this.parseLngLatString(form.originCoord);
      let destGcj = this.lastSearchDestGcj || this.parseLngLatString(form.destCoord);
      if (!originGcj && Array.isArray(leftJoin.selectedOptionsFrist) && leftJoin.selectedOptionsFrist.length) {
        const originCode = leftJoin.selectedOptionsFrist[leftJoin.selectedOptionsFrist.length - 1];
        originGcj = await this.resolveAreaCenterByCode(originCode);
        const address = this.buildAddressFromCascaderCodes(leftJoin.selectedOptionsFrist);
        if (!this.searchOriginText) this.searchOriginText = address
        if (!originGcj && address) originGcj = await this.geocodeAddressToLngLat(address);
      }
      if (!destGcj && Array.isArray(leftJoin.selectedOptionsLast) && leftJoin.selectedOptionsLast.length) {
        const destCode = leftJoin.selectedOptionsLast[leftJoin.selectedOptionsLast.length - 1];
        destGcj = await this.resolveAreaCenterByCode(destCode);
        const address = this.buildAddressFromCascaderCodes(leftJoin.selectedOptionsLast);
        if (!this.searchDestText) this.searchDestText = address
        if (!destGcj && address) destGcj = await this.geocodeAddressToLngLat(address);
      }
      this.lastSearchOriginGcj = originGcj;
      this.lastSearchDestGcj = destGcj;
    },
    renderSearchEndpointMarkers() {
      if (this.hideSearchEndpointMarkers) {
        this.clearSearchEndpointMarkers()
        return
      }
      if (!this.mapInstance || !this.AMap) return;
      if (this.searchOriginMarker) {
        this.searchOriginMarker.setMap(null);
        this.searchOriginMarker = null;
      }
      if (this.searchDestMarker) {
        this.searchDestMarker.setMap(null);
        this.searchDestMarker = null;
      }
      if (this.lastSearchOriginGcj) {
        this.searchOriginMarker = new this.AMap.Marker({
          position: this.lastSearchOriginGcj,
          anchor: "bottom-center",
          zIndex: 188,
          title: "始发地",
          icon: new this.AMap.Icon({
            size: new this.AMap.Size(30, 35),
            image: iconZhuang,
            imageSize: new this.AMap.Size(30, 35)
          })
        });
        this.searchOriginMarker.setMap(this.mapInstance);
        this.searchOriginMarker.on("click", () => {
          const lon = this.lastSearchOriginGcj && this.lastSearchOriginGcj[0]
          const lat = this.lastSearchOriginGcj && this.lastSearchOriginGcj[1]
          this.showInfoWindow({
            lonlat: this.lastSearchOriginGcj,
            infoData: {
              lon,
              lat,
              spd: 0,
              hgt: 0,
              gpsTime: null,
              popupType: "originSearch",
              addr: this.searchOriginText || '--',
              vno: this.getInfoPlateNo()
            }
          })
        })
      }
      if (this.lastSearchDestGcj) {
        this.searchDestMarker = new this.AMap.Marker({
          position: this.lastSearchDestGcj,
          anchor: "bottom-center",
          zIndex: 187,
          title: "目的地",
          icon: new this.AMap.Icon({
            size: new this.AMap.Size(30, 35),
            image: iconXie,
            imageSize: new this.AMap.Size(30, 35)
          })
        });
        this.searchDestMarker.setMap(this.mapInstance);
        this.searchDestMarker.on("click", () => {
          const lon = this.lastSearchDestGcj && this.lastSearchDestGcj[0]
          const lat = this.lastSearchDestGcj && this.lastSearchDestGcj[1]
          this.showInfoWindow({
            lonlat: this.lastSearchDestGcj,
            infoData: {
              lon,
              lat,
              spd: 0,
              hgt: 0,
              gpsTime: null,
              popupType: "destSearch",
              addr: this.searchDestText || '--',
              vno: this.getInfoPlateNo()
            }
          })
        })
      }
    },

    clearIndependentPositionMarkers() {
      this.independentPositionMarkers.forEach(record => record.marker.setMap(null))
      this.independentPositionMarkers = []
    },
    syncIndependentPositionMarkers() {
      if (!this.mapInstance || !this.AMap) return
      this.clearIndependentPositionMarkers()
      const vehicles = this.vehiclePositions.filter(vehicle => vehicle && vehicle.longitude != null && vehicle.latitude != null && Number.isFinite(Number(vehicle.longitude)) && Number.isFinite(Number(vehicle.latitude)))
      vehicles.forEach(vehicle => {
        const position = coordtransform.wgs84togcj02(Number(vehicle.longitude), Number(vehicle.latitude))
        const marker = new this.AMap.Marker({ map: this.mapInstance, position, icon: new this.AMap.Icon({ image: carIcon, size: new this.AMap.Size(32, 32), imageSize: new this.AMap.Size(32, 32) }), offset: new this.AMap.Pixel(-16, -16), zIndex: vehicle.key === this.selectedVehicleKey ? 250 : 120 })
        const plate = String(vehicle.vehicleNo).replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]))
        const color = { '1': '蓝色', '2': '黄色', '3': '黄绿色' }[vehicle.plateColor] || '未知颜色'
        marker.setLabel({ content: `${vehicle.key === this.selectedVehicleKey ? '【已选】' : ''}${plate} · ${color}`, direction: 'top' })
        marker.on('click', () => {
          this.$emit('vehicle-selected', vehicle.key)
          this.focusIndependentVehicle(vehicle.key)
          this.showInfoWindow({ lonlat: position, infoData: { lon: position[0], lat: position[1], spd: vehicle.speed, gpsTime: vehicle.locationTime, agl: vehicle.direction, addr: vehicle.address || '未解析', vno: `${vehicle.vehicleNo} · ${color}` } })
        })
        this.independentPositionMarkers.push({ key: vehicle.key, marker, position, labelText: `${plate} · ${color}` })
      })
      const signature = vehicles.map(vehicle => `${vehicle.key}:${vehicle.longitude}:${vehicle.latitude}`).join('|')
      if (vehicles.length && signature !== this.independentPositionSignature) this.mapInstance.setFitView(this.independentPositionMarkers.map(record => record.marker), false, [50, 50, 50, 50])
      this.independentPositionSignature = signature
    },
    focusIndependentVehicle(key) {
      this.independentPositionMarkers.forEach(record => {
        record.marker.setzIndex(record.key === key ? 250 : 120)
        record.marker.setLabel({ content: `${record.key === key ? '【已选】' : ''}${record.labelText}`, direction: 'top' })
      })
      const record = this.independentPositionMarkers.find(record => record.key === key)
      if (record && this.mapInstance) this.mapInstance.setCenter(record.position)
    },

    // 将轨迹点渲染为 PathSimplifier 的 path，并创建导航器
    clearJointPlayback() {
      if (this.jointPlayTimer) window.clearInterval(this.jointPlayTimer)
      this.jointPlayTimer = null
      if (this.jointVehicleMarker) this.jointVehicleMarker.setMap(null)
      this.jointVehicleMarker = null
    },
    renderJointTrackPath() {
      this.clearJointPlayback()
      if (!this.traceList.length) return
      this.playbackClipStartRatio = 0
      this.playbackClipEndRatio = 1
      if (this.navg) {
        this.navg.stop()
        this.navg.destroy()
        this.navg = null
      }
      this.syncJointTrackPath()
      this.jointVehicleMarker = new this.AMap.Marker({ map: this.mapInstance, position: [this.traceList[0].lon, this.traceList[0].lat], icon: new this.AMap.Icon({ image: carIcon, size: new this.AMap.Size(32, 32), imageSize: new this.AMap.Size(32, 32) }), offset: new this.AMap.Pixel(-16, -16), zIndex: 120 })
      this.jointVehicleMarker.on('click', () => {
        const located = locateJointPlayback(this.traceList, this.jointPlayRatio)
        if (located) this.$emit('navigator-vehicle-click', { ratio: located.navigatorRatio, trackIndex: located.trackIndex, navPos: { lon: located.position[0], lat: located.position[1] } })
      })
      if (this.hideNavigatorVehicle) this.jointVehicleMarker.hide()
      this.jointPlayRatio = 0
      this.isCanPlay = this.traceList.length > 1
      this.scheduleFitMapToTrack()
    },
    syncJointTrackPath() {
      const { startIdx, sliced } = slicePointsByRatioRange(this.traceList, this.playbackClipStartRatio, this.playbackClipEndRatio)
      const groups = []
      sliced.forEach((point, index) => {
        if (!index || point.segmentKey !== sliced[index - 1].segmentKey) groups.push({ name: '联合轨迹', path: [], pointStartIndex: startIdx + index })
        groups[groups.length - 1].path.push([point.lon, point.lat])
      })
      this.pathSimplifierIns.setData(groups)
      this.pathSimplifierIns.show()
    },
    updateJointPlaybackPosition() {
      const located = locateJointPlayback(this.traceList, this.jointPlayRatio)
      if (!located || !this.jointVehicleMarker) return
      const current = this.traceList[located.trackIndex]
      const next = this.traceList[located.trackIndex + 1]
      const target = Number(this.traceList[0].gpsTime) + this.jointPlayRatio * (Number(this.traceList[this.traceList.length - 1].gpsTime) - Number(this.traceList[0].gpsTime))
      if (this.qualityMode && next && next.segmentKey !== current.segmentKey && target > Number(current.gpsTime) && target < Number(next.gpsTime)) {
        this.jointVehicleMarker.hide()
        const ratio = (located.trackIndex + (target - Number(current.gpsTime)) / (Number(next.gpsTime) - Number(current.gpsTime))) / (this.traceList.length - 1)
        this.$emit('quality-gap', { inGap: true, startTime: current.gpsTime, endTime: next.gpsTime })
        this.$emit('nav-progress', { ratio, inGap: true, navPos: null })
        return
      }
      if (this.qualityMode && !this.hideNavigatorVehicle) this.jointVehicleMarker.show()
      this.jointVehicleMarker.setPosition(located.position)
      this.$emit('nav-progress', { ratio: located.navigatorRatio, trackIndex: located.trackIndex, navPos: { lon: located.position[0], lat: located.position[1] } })
    },
    controlJointPlayback(payload) {
      if (payload.speedMult != null) this.jointSpeed = Math.max(1, Number(payload.speedMult) || 1)
      if (payload.type === 'speed') return
      if (this.jointPlayTimer) window.clearInterval(this.jointPlayTimer)
      this.jointPlayTimer = null
      if (payload.type === 'seek') {
        if (payload.rangeReset) return
        this.jointPlayRatio = jointNavigatorTimeRatio(this.traceList, Number(payload.ratio) || 0)
      }
      if (payload.type === 'play') {
        if (payload.fromWindowStart || this.jointPlayRatio >= jointNavigatorTimeRatio(this.traceList, this.playbackClipEndRatio)) this.jointPlayRatio = jointNavigatorTimeRatio(this.traceList, this.playbackClipStartRatio)
        else if (payload.ratio != null) this.jointPlayRatio = jointNavigatorTimeRatio(this.traceList, Number(payload.ratio) || 0)
        this.playStatus = 'start'
        let previous = Date.now()
        this.jointPlayTimer = window.setInterval(() => {
          const now = Date.now()
          const duration = Number(this.traceList[this.traceList.length - 1].gpsTime) - Number(this.traceList[0].gpsTime)
          const endRatio = jointNavigatorTimeRatio(this.traceList, this.playbackClipEndRatio)
          this.jointPlayRatio = Math.min(endRatio, this.jointPlayRatio + (now - previous) * this.jointSpeed / Math.max(1, duration))
          previous = now
          const located = locateJointPlayback(this.traceList, this.jointPlayRatio)
          const next = located && this.traceList[located.trackIndex + 1]
          // 播放直接越过两单空档，车辆不沿空白区间插值移动。
          if (next && next.segmentKey !== this.traceList[located.trackIndex].segmentKey) {
            if (this.qualityMode) this.$emit('quality-gap', { skippedSeconds: (Number(next.gpsTime) - Number(this.traceList[located.trackIndex].gpsTime)) / 1000 })
            this.jointPlayRatio = Math.min(endRatio, jointNavigatorTimeRatio(this.traceList, (located.trackIndex + 1) / (this.traceList.length - 1)))
          }
          this.updateJointPlaybackPosition()
          if (this.jointPlayRatio >= endRatio) {
            window.clearInterval(this.jointPlayTimer)
            this.jointPlayTimer = null
            this.playStatus = 'stop'
          }
        }, 80)
      } else this.playStatus = 'pause'
      this.updateJointPlaybackPosition()
    },
    renderTrackPath(tracePoints) {
      if (tracePoints.length === 0) return;

      this.playbackClipStartRatio = 0
      this.playbackClipEndRatio = 1

      if (this.navg) {
        try {
          if (typeof this.navg.off === 'function') {
            try { this.navg.off('move', this.handleNavgMove) } catch (e) { /* ignore */ }
            try { this.navg.off('pause', this.handleNavgPause) } catch (e) { /* ignore */ }
          }
          if (typeof this.navg.stop === 'function') this.navg.stop()
          if (typeof this.navg.destroy === 'function') this.navg.destroy()
        } catch (e) {
          /* ignore */
        }
        this.navg = null
      }

      // PathSimplifier 期望 path 为二维数组：[ [lon, lat], ... ]
      const path = tracePoints.map(point => [point.lon, point.lat]);
      this.currentTrackPath = path;
      const trackName = `更新时间：${this.formatPopupUpdateTime(tracePoints[0])}`;
      this.currentTrackTitle = trackName;
      this.isCanPlay = true;

      // 设置轨迹数据并显示
      this.pathSimplifierIns.show();
      this.refreshSpeedTrajectoryOverlay();

      if (!this.navg) {
        this.createTrackPathNavigator()
      }

      // 起终点 marker 定位
      const firstPoint = tracePoints[0];
      const lastPoint = tracePoints[tracePoints.length - 1];
      this.markerStart.setPosition([firstPoint.lon, firstPoint.lat]);
      this.markerEnd.setPosition([lastPoint.lon, lastPoint.lat]);
      this.syncEndpointMarkersVisibility();
      this.applyEndpointDisplayOverride();

      // PathSimplifier 轨迹不在 setFitView overlay 内，按轨迹点 bounds 自适应视野
      if (this.pathSimplifierIns && typeof this.pathSimplifierIns.renderLater === 'function') {
        this.pathSimplifierIns.renderLater();
      }
      this.scheduleFitMapToTrack();

      // 计算总距离/总时长（用于进度与时间换算）
      this.distanceAll = this.AMap.GeometryUtil.distanceOfLine(path);
      this.barEndTimes = (this.distanceAll / this.baseSpeed) * 1000;
      this.barEndTime = formatTime(this.barEndTimes);
      if (this.hidePathLineOnly) this.applyPathLineRenderVisibility()
    },

    getSpeedPointMarkerRegistry() {
      if (!this.speedColoredPointMarkerRegistry) {
        this.speedColoredPointMarkerRegistry = new Set()
      }
      return this.speedColoredPointMarkerRegistry
    },
    getSpeedTrajMapHost() {
      return {
        pathSimplifierIns: this.pathSimplifierIns,
        map: this.mapInstance,
        AMap: this.AMap,
        polylines: this.speedColoredPolylines,
        pointMarkers: this.speedColoredPointMarkers,
        pointMarkerRegistry: this.getSpeedPointMarkerRegistry(),
        hidePathLineOnly: this.hidePathLineOnly,
        forceSolidTrackColor: this.forceSolidTrackColor,
        getPoints: () => this.traceList,
        getTrackName: () => this.currentTrackTitle,
        getPlaybackClip: () => ({
          startRatio: this.playbackClipStartRatio,
          endRatio: this.playbackClipEndRatio
        }),
        refreshOverlay: () => this.refreshSpeedTrajectoryOverlay()
      }
    },
    applyPlaybackRangeClip(payload) {
      if (!payload || !this.traceList.length) return
      const sr = Math.min(1, Math.max(0, Number(payload.startRatio) || 0))
      const er = Math.min(1, Math.max(sr, Number(payload.endRatio) || 1))
      this.playbackClipStartRatio = sr
      this.playbackClipEndRatio = er
      if (this.trackData && this.trackData.segmentKeys) {
        this.controlJointPlayback({ type: 'pause' })
        this.syncJointTrackPath()
        this.jointPlayRatio = jointNavigatorTimeRatio(this.traceList, sr)
        this.updateJointPlaybackPosition()
        this.applyEndpointDisplayOverride()
        this.syncParkingMarkersForPlaybackClip()
        return
      }
      this.pauseDistance = 0
      this.refreshSpeedTrajectoryOverlay(true)
      this.applyEndpointDisplayOverride()
      this.resetNavigatorToClipWindowStart()
      if (this.showTrackParkingDetailList && this.showTrackParkingDetailList.length) {
        if (!this.syncParkingMarkersForPlaybackClip()) {
          this.renderParkingMarkers()
        }
      }
    },
    syncPauseDistanceFromNavigator() {
      if (!this.navg) return
      try {
        const distance = Number(this.navg.getMovedDistance())
        if (Number.isFinite(distance) && distance >= 0) {
          this.pauseDistance = distance
        }
      } catch (e) {
        /* ignore */
      }
    },
    /** PathSimplifier 不在 setFitView 覆盖物内，按轨迹点 bounds 包住整段（含起终点文字条留白）
     * @param {Array} pathPoints
     * @param {Array} extras
     * @param {{ capture?: boolean }} options capture=true 时用更大留白并立即 fit，供截图导出
     */
    fitMapBoundsToLngLats(pathPoints, extras = [], options = {}) {
      if (this.mapViewFitLocked) return
      if (!this.mapInstance || !this.AMap) return
      const all = (pathPoints || [])
        .concat(extras || [])
        .map((item) => [Number(item[0]), Number(item[1])])
        .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat))
      if (!all.length) return

      let minLng = all[0][0]
      let maxLng = all[0][0]
      let minLat = all[0][1]
      let maxLat = all[0][1]
      all.forEach(([lng, lat]) => {
        minLng = Math.min(minLng, lng)
        maxLng = Math.max(maxLng, lng)
        minLat = Math.min(minLat, lat)
        maxLat = Math.max(maxLat, lat)
      })

      let dLng = maxLng - minLng
      let dLat = maxLat - minLat
      const span = Math.max(dLng, dLat)
      const capture = !!(options && options.capture)
      // 截图：完整入画即可，留白适中（过大轨迹过小，过小会裁起终点）
      const padRatio = capture
        ? (span > 3 ? 0.16 : span > 1 ? 0.14 : 0.12)
        : (span > 3 ? 0.18 : span > 1 ? 0.14 : 0.12)
      if (dLng < 1e-6) dLng = 0.015
      if (dLat < 1e-6) dLat = 0.015
      minLng -= dLng * padRatio
      maxLng += dLng * padRatio
      minLat -= dLat * padRatio
      maxLat += dLat * padRatio

      const bounds = new this.AMap.Bounds(
        new this.AMap.LngLat(minLng, minLat),
        new this.AMap.LngLat(maxLng, maxLat)
      )
      // setBounds avoid 顺序：上、下、左、右
      const padding = capture
        ? (span > 3 ? [64, 80, 100, 100] : [56, 72, 90, 90])
        : (span > 3 ? [80, 100, 120, 120] : [64, 80, 100, 100])
      const immediately = capture || !!(options && options.immediately)
      const applyFit = () => {
        if (!this.mapInstance) return
        try {
          // 优先用隐形折线 + setFitView，比纯 setBounds 更稳（含覆盖物）
          if (capture && typeof this.mapInstance.setFitView === 'function' && all.length >= 2) {
            const tempLine = new this.AMap.Polyline({
              path: all,
              strokeOpacity: 0,
              strokeWeight: 1,
              zIndex: 1,
              bubble: true
            })
            this.mapInstance.add(tempLine)
            const overlays = [tempLine]
            if (this.markerStart) overlays.push(this.markerStart)
            if (this.markerEnd) overlays.push(this.markerEnd)
            if (this.markerStartLabel) overlays.push(this.markerStartLabel)
            if (this.markerEndLabel) overlays.push(this.markerEndLabel)
            try {
              this.mapInstance.setFitView(overlays, true, padding, 18)
            } finally {
              try { this.mapInstance.remove(tempLine) } catch (e) { /* ignore */ }
            }
          } else {
            this.mapInstance.setBounds(bounds, immediately, padding)
          }
        } catch (e) {
          try {
            this.mapInstance.setBounds(bounds, immediately, padding)
          } catch (e2) {
            /* ignore */
          }
        }
      }
      applyFit()
      if (!capture) {
        this.$nextTick(applyFit)
        setTimeout(applyFit, 150)
        setTimeout(applyFit, 400)
      }
    },
    collectTrackOverlayLngLats() {
      const extras = []
      const pushPos = (pos) => {
        if (!pos) return
        const lon = pos.lng != null ? pos.lng : (typeof pos.getLng === 'function' ? pos.getLng() : NaN)
        const lat = pos.lat != null ? pos.lat : (typeof pos.getLat === 'function' ? pos.getLat() : NaN)
        if (Number.isFinite(lon) && Number.isFinite(lat)) extras.push([lon, lat])
      }
      if (this.markerStart && typeof this.markerStart.getPosition === 'function') {
        pushPos(this.markerStart.getPosition())
      }
      if (this.markerEnd && typeof this.markerEnd.getPosition === 'function') {
        pushPos(this.markerEnd.getPosition())
      }
      return extras
    },
    scheduleFitMapToTrack() {
      if (this.mapViewFitLocked) return
      this.$nextTick(() => {
        if (this.mapViewFitLocked) return
        this.fitMapToAllTrackOverlays()
        setTimeout(() => {
          if (this.mapViewFitLocked) return
          this.fitMapToAllTrackOverlays()
        }, 150)
      })
    },
    fitMapToAllTrackOverlays(options = {}) {
      if (this.mapViewFitLocked) return
      if (!this.mapInstance || !this.AMap) return
      const pathPoints = (this.traceList || []).map((point) => [Number(point.lon), Number(point.lat)])
      const extras = this.collectTrackOverlayLngLats()
      if (pathPoints.length) {
        this.fitMapBoundsToLngLats(pathPoints, extras, options)
        return
      }
      if (extras.length) {
        this.fitMapBoundsToLngLats(extras, [], options)
      }
    },
    /** 截图/导出前准备地图。
     * preserveView=true：折叠左侧后只 resize 并恢复原 zoom/center，禁止 setFitView（否则会突然放大裁切）
     */
    async prepareMapForCapture(options = {}) {
      this.closeInfoWindowQuiet()
      const map = this.mapInstance
      if (!map) return

      const preserveView = !!(options && options.preserveView)
      let savedSnap = null
      if (preserveView) {
        savedSnap = this.snapshotMapView()
      }

      try {
        if (typeof map.resize === 'function') map.resize()
      } catch (e) {
        /* ignore */
      }
      await new Promise((resolve) => setTimeout(resolve, 200))

      if (preserveView) {
        this.restoreMapView(savedSnap)
        if (this.pathSimplifierIns && typeof this.pathSimplifierIns.renderLater === 'function') {
          this.pathSimplifierIns.renderLater()
        }
        await new Promise((resolve) => setTimeout(resolve, 300))
        return
      }

      this.fitMapToAllTrackOverlays({ capture: true, immediately: true })
      await new Promise((resolve) => setTimeout(resolve, 300))

      if (this.pathSimplifierIns && typeof this.pathSimplifierIns.renderLater === 'function') {
        this.pathSimplifierIns.renderLater()
      }
      await new Promise((resolve) => setTimeout(resolve, 400))
    },
    /** 截图期间禁止 setFitView/setBounds，避免左侧折叠后突然放大 */
    lockMapViewFit() {
      this.mapViewFitLocked = true
    },
    unlockMapViewFit() {
      this.mapViewFitLocked = false
    },
    /** 折叠左侧前调用：记下当前视野，折叠后用于恢复，避免突然放大 */
    snapshotMapView() {
      const map = this.mapInstance
      if (!map) return null
      try {
        const zoom = Number(map.getZoom && map.getZoom())
        const center = map.getCenter && map.getCenter()
        if (!Number.isFinite(zoom) || !center) return null
        const lng = typeof center.getLng === 'function' ? Number(center.getLng()) : Number(center.lng)
        const lat = typeof center.getLat === 'function' ? Number(center.getLat()) : Number(center.lat)
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null
        return { zoom, lng, lat }
      } catch (e) {
        return null
      }
    },
    restoreMapView(snapshot) {
      const map = this.mapInstance
      if (!map || !snapshot) return
      try {
        const zoom = Number(snapshot.zoom)
        const lng = Number(snapshot.lng != null ? snapshot.lng : (snapshot.center && snapshot.center.lng))
        const lat = Number(snapshot.lat != null ? snapshot.lat : (snapshot.center && snapshot.center.lat))
        let center = null
        if (Number.isFinite(lng) && Number.isFinite(lat)) {
          center = this.AMap ? new this.AMap.LngLat(lng, lat) : [lng, lat]
        } else if (snapshot.center) {
          center = snapshot.center
        }
        if (!Number.isFinite(zoom) || !center) return
        if (typeof map.setZoomAndCenter === 'function') {
          map.setZoomAndCenter(zoom, center, true)
        } else {
          if (typeof map.setZoom === 'function') map.setZoom(zoom, true)
          if (typeof map.setCenter === 'function') map.setCenter(center, true)
        }
      } catch (e) {
        /* ignore */
      }
    },
    getNavigatorPathIndex() {
      if (!this.pathSimplifierIns) return 0
      const flaggedIdx = getSpeedNavPathIndex(this.pathSimplifierIns)
      if (isSpeedTrajectoryColorMode() || flaggedIdx > 0) {
        return flaggedIdx
      }
      return 0
    },
    refreshSpeedTrajectoryOverlay(skipSyncPause) {
      if (this.trackData && this.trackData.segmentKeys) return
      if (!this.mapInstance || !this.traceList.length) return
      if (!skipSyncPause) {
        this.syncPauseDistanceFromNavigator()
      }
      const shouldRestoreNav = !!this.navg || this.isCanPlay
      syncSpeedTrajectoryMap(this.getSpeedTrajMapHost())
      if (shouldRestoreNav) {
        this.rebuildNavigatorPassedStyle()
      }
    },
    createTrackPathNavigator() {
      if (!this.pathSimplifierIns) return
      this.navg = this.pathSimplifierIns.createPathNavigator(this.getNavigatorPathIndex(), {
        loop: false,
        speed: this.baseSpeed,
        pathNavigatorStyle: {
          width: 32,
          height: 32,
          autoRotate: true,
          initRotateDegree: -90,
          strokeStyle: null,
          fillStyle: null,
          content: this.getPathNavigatorCarImageContent(),
          pathLinePassedStyle: this.getNavigatorPathLinePassedStyle()
        }
      })
      this.navg.on('move', this.handleNavgMove)
      this.navg.on('pause', this.handleNavgPause)
      this.handleSpeedChange(this.speedList[1])
    },
    applyTrajectoryColorSettings(cfg) {
      const settings = cfg || getTrajectoryColorSettings()
      const modeChanged = this._trajectoryColorMode !== settings.mode
      const wasSpeedMode = this._trajectoryColorMode === TRAJECTORY_COLOR_MODE_SPEED
      this._trajectoryColorMode = settings.mode
      this.syncPauseDistanceFromNavigator()
      if (settings.mode === TRAJECTORY_COLOR_MODE_SPEED) {
        this.createPathSimplifier(null, { reloadPath: true })
        return
      }
      if (wasSpeedMode || modeChanged) {
        clearSpeedTrajectoryPolylines(this.getSpeedTrajMapHost())
      }
      this.createPathSimplifier(settings.solidColor, { reloadPath: modeChanged })
    },

    // 导航器 move 回调：同步“进度/时间”，并更新信息窗位置与内容
    handleNavgMove() {
      const movedDistance = this.navg.getMovedDistance();
      const navTotal = this.getNavigatorDistanceAll();
      const clipLocalRatio = navTotal > 0 ? movedDistance / navTotal : 0;
      const fullRatio = this.mapClipLocalToFullRatio(clipLocalRatio);
      this.sliderVal = fullRatio * 100;

      const playedTime = fullRatio * this.barEndTimes;
      this.barStartTime = formatTime(playedTime);

      const currentPos = this.navg.getPosition();
      const normalData = this.getNormalizedData();
      this.syncTrackUpdateTime(normalData.normalInfo);
      // 默认不自动弹出 InfoWindow；仅当已打开（用户点过）或显式关闭抑制时才跟随更新
      if (!this.hideNavigatorVehicle && (this.isInfoWindowOpen() || !this.suppressAutoInfoWindow)) {
        this.showInfoWindow({
          lonlat: currentPos,
          // 这里暂时用经纬度字符串充当 addr（真实地址在 setGeocoderAddress 中异步反查后更新）
          infoData: { ...normalData.normalInfo, addr: normalData.normalLonLat, vno: this.getInfoPlateNo() }
        });
      }
      if (!this._suppressNavProgressEmit) {
        this.$emit("nav-progress", {
          ratio: fullRatio,
          navPos: {
            lon: currentPos && currentPos.lng != null ? currentPos.lng : currentPos[0],
            lat: currentPos && currentPos.lat != null ? currentPos.lat : currentPos[1]
          }
        });
      }
      this.maybeFocusVehicle(
        currentPos && currentPos.lng != null ? currentPos.lng : currentPos[0],
        currentPos && currentPos.lat != null ? currentPos.lat : currentPos[1]
      );
    },

    // 从当前游标向前找最近一个“有效点”（gtm 存在的点）
    getPreviousNormalTraceIndex() {
      const currentIdx = this.resolveNavigatorTraceIndex();
      const { startIdx } = getPlaybackClipIndices(
        this.traceList,
        this.playbackClipStartRatio,
        this.playbackClipEndRatio
      );
      const lo = this.isPlaybackClipActive() ? startIdx : 0;
      for (let i = currentIdx; i >= lo; i--) {
        if (this.traceList[i] && this.traceList[i].gtm) return i;
      }
      return lo;
    },

    // 统一返回用于展示的信息：normalInfo（点数据）与 normalLonLat（格式化经纬度）
    getNormalizedData() {
      const currentIdx = this.resolveNavigatorTraceIndex();
      const prevIdx = this.getPreviousNormalTraceIndex();
      const normalInfo = JSON.parse(JSON.stringify(this.traceList[prevIdx]));

      // 当前点无 gtm 时，用前后有效点计算一个“模拟速度”用于展示
      if (!this.traceList[currentIdx] || !this.traceList[currentIdx].gtm) {
        normalInfo.spd = this.calculateSvgSpeed().toFixed(2);
      }

      const lonStr = Number.isFinite(Number(normalInfo.lon)) ? Number(normalInfo.lon).toFixed(6) : "--";
      const latStr = Number.isFinite(Number(normalInfo.lat)) ? Number(normalInfo.lat).toFixed(6) : "--";
      return {
        normalInfo,
        normalLonLat: `${lonStr},${latStr}`
      };
    },

    // 计算模拟速度：取前后有效点算距离/时长，再做上下限修正
    calculateSvgSpeed() {
      const { prevIdx, nextIdx } = this.getAdjacentNormalTraceIndices();
      const pathPoints = this.traceList.slice(prevIdx, nextIdx + 1);
      const path = pathPoints.map(p => [p.lon, p.lat]);
      const distance = this.AMap.GeometryUtil.distanceOfLine(path) / 1000;

      const startTime = Number(this.traceList[prevIdx].gpsTime);
      const endTime = Number(this.traceList[nextIdx].gpsTime);
      const hours = (endTime - startTime) / 3600000;

      const speed = distance / hours;
      return speed > 100 ? 80 : (speed < 5 ? 5 : speed);
    },

    // 从当前游标向前/向后找到最近的有效点索引（gtm 存在的点）
    getAdjacentNormalTraceIndices() {
      const currentIdx = this.resolveNavigatorTraceIndex();
      const { startIdx, endIdx } = getPlaybackClipIndices(
        this.traceList,
        this.playbackClipStartRatio,
        this.playbackClipEndRatio
      );
      const lo = this.isPlaybackClipActive() ? startIdx : 0;
      const hi = this.isPlaybackClipActive() ? endIdx : this.traceList.length - 1;
      let prevIdx = lo, nextIdx = hi;

      for (let i = currentIdx; i >= lo; i--) {
        if (this.traceList[i] && this.traceList[i].gtm) {
          prevIdx = i;
          break;
        }
      }

      for (let i = currentIdx; i <= hi; i++) {
        if (this.traceList[i] && this.traceList[i].gtm) {
          nextIdx = i;
          break;
        }
      }

      return { prevIdx, nextIdx };
    },

    // 导航器 pause 回调：到达最后一个点则停止并置为 stop
    handleNavgPause() {
      if (this._pathSeeking) return;
      const movedDistance = this.navg.getMovedDistance();
      const navTotal = this.getNavigatorDistanceAll();
      const clipLocalRatio = navTotal > 0 ? movedDistance / navTotal : 0;
      const fullRatio = this.mapClipLocalToFullRatio(clipLocalRatio);
      let cursorIdx = -1;
      try {
        const cursor =
          typeof this.navg.getCursor === 'function' ? this.navg.getCursor() : this.navg.cursor;
        cursorIdx = cursor && cursor.idx != null ? Number(cursor.idx) : -1;
      } catch (e) {
        cursorIdx = -1;
      }
      const pathEndIdx =
        typeof this.navg.getPathEndIdx === 'function'
          ? Number(this.navg.getPathEndIdx())
          : this.traceList.length - 1;
      if (!this.isPlaybackClipActive()) {
        // 用巡航器路径终点下标判断，勿用全量 traceList.length（裁剪/抽稀时会误判为终点并 stop）
        if (cursorIdx >= 0 && cursorIdx >= pathEndIdx && clipLocalRatio >= 1 - 1e-3) {
          this.navg.stop();
          this.playStatus = "stop";
          this.pauseDistance = 0;
        }
      } else if (clipLocalRatio >= 1 - 1e-6) {
        this.navg.stop();
        this.playStatus = "stop";
        this.pauseDistance = 0;
      }
      if (this._suppressNavProgressEmit) return;
      const currentPos = this.navg.getPosition();
      this.$emit("nav-progress", {
        ratio: fullRatio,
        navPos: {
          lon: currentPos && currentPos.lng != null ? currentPos.lng : currentPos[0],
          lat: currentPos && currentPos.lat != null ? currentPos.lat : currentPos[1]
        }
      });
    },

    // 调整倍速：同步 baseSpeed 与 navg 的运行速度
    handleSpeedChange(speed) {
      this.navg && this.navg.setSpeed(this.baseSpeed * speed.value);
      this.speedName = speed.label;
      this.speedMod = false;
    },
    // 供父页面转发右侧组件控制事件（play/pause/seek/speed）
    onPathNavControl(payload) {
      if (this.trackData && this.trackData.segmentKeys && payload) {
        this.controlJointPlayback(payload)
        return
      }
      if (!payload || !this.navg) return;
      const type = payload.type;
      if (type === "play") {
        if (payload.speedMult != null) {
          const mult = Math.max(1, Number(payload.speedMult) || 1)
          this.navg.setSpeed(this.baseSpeed * mult)
          this.speedName = `${mult}X`
        }
        this.tracePointPreviewActive = false;
        this.clearPreviewVehicleMarker();
        if (payload.fromWindowStart === true) {
          this.pauseDistance = 0;
          // handlePlay 会从当前裁剪路径起点启动；这里不能预先 pause，避免异步 pause 事件延迟把进度重置为零
          this.playStatus = "stop";
        } else if (payload.ratio != null && Number.isFinite(Number(payload.ratio))) {
          // seek 后已是 pause 且车已在落点：勿再按时间比例 seek（会往前跳）
          // 仅在非 pause 时对齐；pause 时刷新真实 pauseDistance 后直接续播
          if (this.playStatus !== 'pause') {
            const ratio = Math.min(1, Math.max(0, Number(payload.ratio) || 0));
            this.sliderVal = ratio * 100;
            this.handleSliderChange();
          } else {
            try {
              const live = Number(this.navg.getMovedDistance());
              if (Number.isFinite(live) && live >= 0) this.pauseDistance = live;
            } catch (e) {
              /* ignore */
            }
          }
        } else if (this.playStatus === "stop") {
          this.pauseDistance = 0;
          const startRatio = this.isPlaybackClipActive() ? this.playbackClipStartRatio : 0;
          this.sliderVal = startRatio * 100;
          this.handleSliderChange();
        }
        this.handlePlay();
        const pos = this.navg.getPosition ? this.navg.getPosition() : null;
        if (pos) {
          this.maybeFocusVehicle(pos.lng != null ? pos.lng : pos[0], pos.lat != null ? pos.lat : pos[1], { force: true });
        }
        return;
      }
      if (type === "pause") {
        this.handlePause();
        return;
      }
      if (type === "seek") {
        if (payload.rangeReset === true) return;
        const ratio = Math.min(1, Math.max(0, Number(payload.ratio) || 0));
        this.sliderVal = ratio * 100;
        this.handleSliderChange({ preview: payload.preview === true });
        return;
      }
      if (type === "speed") {
        const mult = Math.max(1, Number(payload.speedMult) || 1);
        this.navg.setSpeed(this.baseSpeed * mult);
        this.speedName = `${mult}X`;
      }
    },

    /** PathSimplifier 由 AMapUI.load 注入到回调参数，需用保存的构造函数 */
    getPathSimplifierClass() {
      if (this.pathSimplifierCtor) return this.pathSimplifierCtor
      if (typeof AMapUI !== 'undefined' && AMapUI.PathSimplifier) return AMapUI.PathSimplifier
      return null
    },
    getPathNavigatorCarImageContent() {
      const PS = this.getPathSimplifierClass()
      if (!PS || !PS.Render || !PS.Render.Canvas || typeof PS.Render.Canvas.getImageContent !== 'function') {
        return null
      }
      return PS.Render.Canvas.getImageContent(carIcon)
    },
    // 初始化 PathSimplifier：轨迹线样式、点样式、hover 标题、以及轨迹点点击事件
    createPathSimplifier(color, options = {}) {
      if(color){
        this.colorChangeLocal = color
      }
      const reloadPath = options.reloadPath === true
      const { c1, c2, c3 } = this.parseTrajectoryColorTriple(this.effectiveColorChange)
      // 已初始化：仅更新颜色并重绘，避免重复 new/重复绑定事件
      if (this.pathSimplifierIns) {
        try {
          const opts =
            typeof this.pathSimplifierIns.getRenderOptions === 'function'
              ? this.pathSimplifierIns.getRenderOptions()
              : null
          this.applyPathSimplifierStrokeColors(opts)
          this.applyUniformTrajectoryPathLineWidths(opts)
          this.applyTrackPointVisibility(opts)
          if (opts) {
            if (isSpeedTrajectoryColorMode()) {
              applySpeedModePathSimplifierTolerance(opts, this.traceList.length)
            } else {
              opts.pathTolerance = 2
              opts.keyPointTolerance = 1
              opts.renderAllPointsIfNumberBelow = 1e4
            }
          }
          if (opts && !opts.getPathStyle) {
            opts.getPathStyle = (pathInfo) => this.getSpeedTrajPathStyle(pathInfo)
          }
          if (typeof this.pathSimplifierIns.renderLater === 'function') {
            this.pathSimplifierIns.renderLater()
          } else if (typeof this.pathSimplifierIns.render === 'function') {
            this.pathSimplifierIns.render()
          }
        } catch (e) {
          /* ignore */
        }
        if (this.hidePathLineOnly) this.applyPathLineRenderVisibility()
        if (reloadPath) {
          this.refreshSpeedTrajectoryOverlay()
        }
        // 同步 estimateRoute 虚线颜色
        if (this.estimateRoutePolyline) {
          try {
            this.estimateRoutePolyline.setOptions({ strokeColor: this.estimateRouteColor || c3 || '#2FAE55' })
          } catch (e) {
            /* ignore */
          }
        }
        return
      }
      // PathSimplifier 渲染样式（沿用原页面的配色/线宽等）
      const pathStyle = {renderAllPointsIfNumberBelow: 1e4,
        pathTolerance: isSpeedTrajectoryColorMode() ? 0 : 2,
        keyPointTolerance: isSpeedTrajectoryColorMode() ? 0 : 1,
        pathLineStyle: {
          lineWidth: TRAJECTORY_PATH_LINE_WIDTH,
          strokeStyle: c3,
          borderWidth: 0,
          borderStyle: 'rgba(0,0,0,0)',
          dirArrowStyle: !1
        },
        pathLineHoverStyle: {
          lineWidth: 0,
          strokeStyle: c2,
          borderWidth: 0,
          borderStyle: null,
          dirArrowStyle: !1
        },
        pathLineSelectedStyle: {
          lineWidth: TRAJECTORY_PATH_LINE_WIDTH,
          strokeStyle: c1,
          borderWidth: 0,
          borderStyle: 'rgba(0,0,0,0)',
          dirArrowStyle: !1
        },
        dirArrowStyle: {
          stepSpace: 35,
          strokeStyle: "#9ca3af",
          lineWidth: 2
        },
        startPointStyle: {
          radius: 4,
          fillStyle: c3,
          lineWidth: 1,
          strokeStyle: "#eeeeee"
        },
        endPointStyle: {
          radius: 4,
          fillStyle: "#dc3912",
          lineWidth: 1,
          strokeStyle: "#eeeeee"
        },
        keyPointStyle: {
          radius: this.hideTrackPoints ? 0 : 2.5,
          fillStyle: c1,
          lineWidth: 1,
          strokeStyle: c1,
          dirArrowStyle: !1
        },
        keyPointHoverStyle: {
          radius: this.hideTrackPoints ? 0 : 3.5,
          fillStyle: c1,
          lineWidth: 2,
          strokeStyle: "#dc3912"
        },
        keyPointOnSelectedPathLineStyle: {
          radius: this.hideTrackPoints ? 0 : 2.5,
          fillStyle: c1,
          lineWidth: 1,
          strokeStyle: c1
        }
      };
      // 隐藏默认起终点/关键点渲染（此处用透明/无边框的样式覆盖）
      var l={
        lineWidth: 0,
        fillStyle: null,
        strokeStyle: null,
        borderStyle: null
      };

      const PathSimplifierClass = this.getPathSimplifierClass()
      if (!PathSimplifierClass) {
        console.error('PathSimplifier 未加载，请确认 ensureMapReady 已完成')
        return
      }
      // 创建 PathSimplifier 实例，并挂到当前地图上
      this.pathSimplifierIns = new PathSimplifierClass({
        zIndex: 100,
        map: this.mapInstance,
        // 默认 true 会在 setData 后强行贴边 fit，长途轨迹起终点地址条易被裁切
        autoSetFitView: false,
        // 每条 path 的坐标数组来源
        getPath: (pathData) => pathData.path,
        // 鼠标 hover 时的标题
        getHoverTitle: (pathData, pathIndex, pointIndex) =>
          this.formatTrajectoryHoverTitle(pathData, pathIndex, pointIndex),
        // 渲染参数
        renderOptions: {
          renderAllPointsIfNumberBelow: pathStyle.renderAllPointsIfNumberBelow,
          pathTolerance: pathStyle.pathTolerance,
          keyPointTolerance: pathStyle.keyPointTolerance,
          pathLineStyle: pathStyle.pathLineStyle,
          pathLineSelectedStyle: pathStyle.pathLineSelectedStyle,
          pathLineHoverStyle: pathStyle.pathLineHoverStyle,
          startPointStyle: l,
          endPointStyle: l,
          keyPointStyle: pathStyle.keyPointStyle,
          keyPointHoverStyle: pathStyle.keyPointHoverStyle,
          keyPointOnSelectedPathLineStyle: pathStyle.keyPointOnSelectedPathLineStyle,
          getPathStyle: (pathInfo) => this.getSpeedTrajPathStyle(pathInfo)
        }
      });

      // 轨迹点/轨迹线点击：将导航器定位到点击点，并异步反查地址更新信息窗
      this.pathSimplifierIns.on("pointClick", (e, info) => {
        this.handlePathClick(e, info)
      })
      this.pathSimplifierIns.on("pathClick", (e, info) => {
        this.handlePathClick(e, info)
      });
      // 轨迹点悬停：底部“更新时间”临时显示为悬停点时间
      this.pathSimplifierIns.on("pointMouseover", (e, pointArg) => {
        const globalIdx = this.resolveTracePointIndex(null, pointArg)
        if (globalIdx < 0) return
        if (this.hoverPointIndex === globalIdx) return;
        this.hoverPointIndex = globalIdx;
        this.suppressHoverOutUntil = Date.now() + 120;
        const hoverUpdate = this.resolveHoverUpdatePoint(null, pointArg);
        // 无有效点时保持当前标题，不置空；仅在到达下一个有效点时更新
        if (!hoverUpdate) return;
        if (this.hoverUpdatePointIndex === hoverUpdate.index) return;
        this.hoverUpdatePointIndex = hoverUpdate.index;
        this.syncTrackUpdateTime(hoverUpdate.point);
      });
      // 悬停移出：恢复为当前导航点（或首点）时间
      this.pathSimplifierIns.on("pointMouseout", () => {
        if (Date.now() < this.suppressHoverOutUntil) return;
        this.hoverPointIndex = -1;
        // 在点与点之间移动鼠标时不立即回退标题，避免中间“无数据点”导致闪烁/消失
      });
    },

    // 基于当前 navg 游标位置执行逆地理编码，并刷新信息窗内容
    async setGeocoderAddress() {
      if (this.hideNavigatorVehicle || (this.suppressAutoInfoWindow && !this.isInfoWindowOpen())) return;
      const currentIdx = this.navg.getCursor().idx;
      const currentPoint = this.traceList[currentIdx];
      const currentPos = this.navg.getPosition();
      const normalData = this.getNormalizedData();
      this.syncTrackUpdateTime(normalData.normalInfo);

      try {
        const address = await this.getAddressByLonLat(currentPos);
        if (this.hideNavigatorVehicle) return;
        this.showInfoWindow({
          lonlat: currentPos,
          infoData: {
            ...normalData.normalInfo,
            gpsStatus: currentPoint.gpsStatus,
            addr: address,
            vno: this.getInfoPlateNo()
          }
        });
      } catch (err) {
        console.error("获取地址失败：", err);
      }
    },
    getEventLngLat(event) {
      const source = event && (event.lnglat || event.lngLat || event.latlng || event.latLng)
      if (Array.isArray(source) && source.length >= 2) {
        const lon = Number(source[0])
        const lat = Number(source[1])
        return Number.isFinite(lon) && Number.isFinite(lat) ? [lon, lat] : null
      }
      const lon = Number(source && (source.lng !== undefined ? source.lng : source.lon))
      const lat = Number(source && (source.lat !== undefined ? source.lat : source.latitude))
      return Number.isFinite(lon) && Number.isFinite(lat) ? [lon, lat] : null
    },
    isNavigatorVehicleClick(event, pointData) {
      if (!this.navg || this.hideNavigatorVehicle || !this.mapInstance) return false
      const selectedIndex = this.resolveTracePointIndex(null, pointData)
      const navigatorIndex = this.resolveNavigatorTraceIndex()
      if (selectedIndex >= 0 && navigatorIndex >= 0 && Math.abs(selectedIndex - navigatorIndex) <= 2) return true
      const clickPoint = this.getEventLngLat(event)
      const navPoint = this.navg.getPosition && this.navg.getPosition()
      if (!clickPoint || !navPoint || typeof this.mapInstance.lngLatToContainer !== 'function') return false
      const navLon = Number(navPoint.lng !== undefined ? navPoint.lng : navPoint[0])
      const navLat = Number(navPoint.lat !== undefined ? navPoint.lat : navPoint[1])
      if (!Number.isFinite(navLon) || !Number.isFinite(navLat)) return false
      try {
        const clickPixel = this.mapInstance.lngLatToContainer(clickPoint)
        const navPixel = this.mapInstance.lngLatToContainer([navLon, navLat])
        const x = Number(clickPixel && clickPixel.x) - Number(navPixel && navPixel.x)
        const y = Number(clickPixel && clickPixel.y) - Number(navPixel && navPixel.y)
        return Number.isFinite(x) && Number.isFinite(y) && Math.sqrt(x * x + y * y) <= 24
      } catch (e) {
        return false
      }
    },
    handleNavigatorVehicleClick() {
      if (!this.navg || !this.navg.getPosition) return
      const position = this.navg.getPosition()
      const lon = Number(position && (position.lng !== undefined ? position.lng : position[0]))
      const lat = Number(position && (position.lat !== undefined ? position.lat : position[1]))
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) return
      const navTotal = this.getNavigatorDistanceAll()
      const movedDistance = Number(this.navg.getMovedDistance && this.navg.getMovedDistance()) || 0
      const normalData = this.getNormalizedData()
      this.$emit('navigator-vehicle-click', {
        ratio: this.mapClipLocalToFullRatio(navTotal > 0 ? movedDistance / navTotal : 0),
        navPos: { lon, lat },
        trackIndex: this.resolveNavigatorTraceIndex()
      })
      // 用户主动点击车标：允许打开 InfoWindow（与 suppressAuto 的「查询默认不弹」区分）
      this.showInfoWindow({
        lonlat: position,
        infoData: { ...normalData.normalInfo, addr: normalData.normalLonLat, vno: this.getInfoPlateNo() }
      })
      this.setGeocoderAddress()
    },
    handlePathClick(event, pointData) {
      if (this.isNavigatorVehicleClick(event, pointData)) {
        this.handleNavigatorVehicleClick()
        return
      }
      this.handleTracePointSeek(null, pointData)
    },

    // 本地缓存 → 全局缓存 → 浏览器地图逆地理。
    getAddressByLonLat(lonlat) {
      return new Promise((resolve) => {
        const parseLonLat = (input) => {
          if (!input) return [NaN, NaN]
          if (Array.isArray(input)) return [Number(input[0]), Number(input[1])]
          if (typeof input.getLng === 'function' && typeof input.getLat === 'function') {
            return [Number(input.getLng()), Number(input.getLat())]
          }
          const lonVal = input.lng != null ? input.lng : input.lon
          const latVal = input.lat
          return [Number(lonVal), Number(latVal)]
        }
        const [lon, lat] = parseLonLat(lonlat)
        if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
          resolve('')
          return
        }
        const coordKey = normalizeCoordKey(lon, lat)
        resolveAddressByCoordUtil(lon, lat, {
          coordKey,
          getLocalCache: (k) => (this.mapAddressCache && this.mapAddressCache[k]) || '',
          setLocalCache: (k, v) => this.$set(this.mapAddressCache, k, v),
          AMap: this.AMap || (typeof window !== 'undefined' ? window.AMap : null),
          geocoder: this.geocoder
        })
          .then((addr) => resolve(isValidResolvedAddress(addr) ? String(addr).trim() : ''))
          .catch(() => resolve(''))
      })
    },
    /** 行程卡与地图上起终点文字条第二行保持同源（避免换算/cache key 差异） */
    emitTripCardSyncedAddressLines() {
      this.$emit('trip-endpoint-address-lines', {
        start: this.tripCardSyncedStartLine,
        end: this.tripCardSyncedEndLine
      })
    },
    /** @param {string|undefined} startLine @param {string|undefined} endLine — undefined 表示不修改该项 */
    setTripCardSyncedLines(startLine, endLine) {
      if (startLine !== undefined) this.tripCardSyncedStartLine = startLine != null ? String(startLine) : ''
      if (endLine !== undefined) this.tripCardSyncedEndLine = endLine != null ? String(endLine) : ''
      this.emitTripCardSyncedAddressLines()
    },
    /** 与 trajectory.vue 的 applyEndpointOverride 对齐（prop 名为 endpointDisplayOverride） */
    applyEndpointDisplayOverride() {
      if (!this.mapInstance) return;
      if (!this.markerStart || !this.markerEnd || !this.markerStartLabel || !this.markerEndLabel) return;
      if (!this.traceList || this.traceList.length === 0) return;
      const clipIndices = getPlaybackClipIndices(
        this.traceList,
        this.playbackClipStartRatio,
        this.playbackClipEndRatio
      );
      const firstPoint = this.traceList[clipIndices.startIdx];
      const lastPoint = this.traceList[clipIndices.endIdx];
      const o =
        this.endpointDisplayOverride && typeof this.endpointDisplayOverride === 'object'
          ? this.endpointDisplayOverride
          : null;
      const clipActive = !isFullPlaybackClip(this.playbackClipStartRatio, this.playbackClipEndRatio);
      if (!o || clipActive) {
        this.markerStart.setPosition([firstPoint.lon, firstPoint.lat]);
        this.markerEnd.setPosition([lastPoint.lon, lastPoint.lat]);
        this.updateEndpointLabels(firstPoint, lastPoint)
          .catch(() => {})
          .finally(() => this.syncEndpointMarkersVisibility());
        if (clipActive) return;
        if (!o) return;
      }
      const s = o.start && typeof o.start === 'object' ? o.start : null;
      const e = o.end && typeof o.end === 'object' ? o.end : null;
      const startLonLat = [firstPoint.lon, firstPoint.lat];
      const endLonLat = [lastPoint.lon, lastPoint.lat];
      const startTimeText =
        s && s.time != null && String(s.time).trim() !== ''
          ? String(s.time).trim()
          : this.formatPopupUpdateTime(firstPoint);
      const endTimeText =
        e && e.time != null && String(e.time).trim() !== ''
          ? String(e.time).trim()
          : this.formatPopupUpdateTime(lastPoint);
      const startLocRaw =
        s && s.location != null && String(s.location).trim() !== '' ? String(s.location).trim() : '';
      const endLocRaw =
        e && e.location != null && String(e.location).trim() !== '' ? String(e.location).trim() : '';
      const startLocText = startLocRaw && isValidResolvedAddress(startLocRaw) ? startLocRaw : '';
      const endLocText = endLocRaw && isValidResolvedAddress(endLocRaw) ? endLocRaw : '';
      const startFallback = `${Number(startLonLat[0]).toFixed(6)},${Number(startLonLat[1]).toFixed(6)}`;
      const endFallback = `${Number(endLonLat[0]).toFixed(6)},${Number(endLonLat[1]).toFixed(6)}`;

      this.markerStart.setPosition([firstPoint.lon, firstPoint.lat]);
      this.markerEnd.setPosition([lastPoint.lon, lastPoint.lat]);

      this.markerStartLabel.setPosition(startLonLat);
      this.markerEndLabel.setPosition(endLonLat);
      this.markerStartLabel.setText(`起：${startTimeText}\n${startLocText || startFallback}`);
      this.markerEndLabel.setText(`终：${endTimeText}\n${endLocText || endFallback}`);
      this.setTripCardSyncedLines(startLocText || startFallback, endLocText || endFallback);
      this.syncEndpointMarkersVisibility();

      const geo = [];
      if (!startLocText) {
        geo.push(
          this.getAddressByLonLat(startLonLat).then((addr) => {
            if (addr) {
              this.markerStartLabel.setText(`起：${startTimeText}\n${addr}`);
              this.setTripCardSyncedLines(addr, undefined);
            }
          })
        );
      }
      if (!endLocText) {
        geo.push(
          this.getAddressByLonLat(endLonLat).then((addr) => {
            if (addr) {
              this.markerEndLabel.setText(`终：${endTimeText}\n${addr}`);
              this.setTripCardSyncedLines(undefined, addr);
            }
          })
        );
      }
      if (geo.length) Promise.all(geo).catch(() => {}).finally(() => this.syncEndpointMarkersVisibility());
    },
    syncParkingMarkersForPlaybackClip() {
      if (!this.parkingMarkerRecords.length) return false
      this.syncParkingMarkersVisibility()
      return true
    },
    syncParkingMarkersVisibility() {
      const records = this.parkingMarkerRecords
      if (records && records.length) {
        const fullClip = !this.isPlaybackClipActive()
        records.forEach(({ marker, label, pointIndex }) => {
          const inClip =
            fullClip ||
            isParkingInPlaybackClip(
              null,
              this.traceList,
              this.playbackClipStartRatio,
              this.playbackClipEndRatio,
              pointIndex
            )
          const visible = inClip && !this.parkingMarkersHidden
          const fn = visible ? 'show' : 'hide'
          try {
            if (marker && typeof marker[fn] === 'function') marker[fn]()
            if (label && typeof label[fn] === 'function') label[fn]()
          } catch (e) {
            /* ignore */
          }
        })
      } else if (this.markerStopList && this.markerStopList.length) {
        const fn = this.parkingMarkersHidden ? 'hide' : 'show'
        this.markerStopList.forEach((m) => {
          if (m && typeof m[fn] === 'function') m[fn]()
        })
      }
      if (!this.infoWindow) return
      const open = typeof this.infoWindow.getIsOpen === 'function' && this.infoWindow.getIsOpen()
      const parkingOpen =
        this._lastInfoPayload &&
        this._lastInfoPayload.infoData &&
        String(this._lastInfoPayload.infoData.popupType || '') === 'parking'
      if (this.parkingMarkersHidden) {
        if (open && parkingOpen) {
          this._parkingInfoRestore = this._lastInfoPayload
          this.infoWindow.close()
        }
      } else if (this._parkingInfoRestore) {
        const payload = this._parkingInfoRestore
        this._parkingInfoRestore = null
        this.showInfoWindow(payload)
      }
    },
    setParkingMarkersHidden(hidden) {
      if (!this.mapInstance) return false
      this.parkingMarkersHidden = !!hidden
      this.syncParkingMarkersVisibility()
      return true
    },
    /** 按当前配色写 PathSimplifier（恢复备份后须调用） */
    applyPathSimplifierStrokeColors(opts) {
      if (!opts) return
      const { c1, c2 } = this.parseTrajectoryColorTriple(this.effectiveColorChange)
      if (!this.hidePathLineOnly) {
        const pl = opts.pathLineStyle
        const ps = opts.pathLineSelectedStyle
        const ph = opts.pathLineHoverStyle
        const bdr = c2 != null ? c2 : c1
        if (pl) {
          pl.strokeStyle = c1
          pl.borderStyle = bdr
        }
        if (ps) {
          ps.strokeStyle = c1
          ps.borderStyle = bdr
        }
        if (ph) ph.strokeStyle = bdr || c1
      }
      if (opts.keyPointStyle) {
        opts.keyPointStyle.fillStyle = c1
        opts.keyPointStyle.strokeStyle = c1
      }
      if (opts.keyPointHoverStyle) {
        opts.keyPointHoverStyle.fillStyle = c1
      }
      if (opts.keyPointOnSelectedPathLineStyle) {
        opts.keyPointOnSelectedPathLineStyle.fillStyle = c1
        opts.keyPointOnSelectedPathLineStyle.strokeStyle = c1
      }
    },
    getNavigatorPathLinePassedStyle() {
      return this.hidePathLineOnly
        ? { ...PATH_LINE_HIDE }
        : {
            lineWidth: TRAJECTORY_PATH_LINE_WIDTH,
            borderWidth: 0,
            fillStyle: null,
            strokeStyle: '#9ca3af',
            borderStyle: 'rgba(0,0,0,0)',
            dirArrowStyle: false
          }
    },
    applyUniformTrajectoryPathLineWidths(opts) {
      if (!opts) return
      const w = TRAJECTORY_PATH_LINE_WIDTH
      if (opts.pathLineStyle) {
        opts.pathLineStyle.lineWidth = w
        opts.pathLineStyle.borderWidth = 0
        opts.pathLineStyle.dirArrowStyle = false
      }
      if (opts.pathLineSelectedStyle) {
        opts.pathLineSelectedStyle.lineWidth = w
        opts.pathLineSelectedStyle.borderWidth = 0
        opts.pathLineSelectedStyle.dirArrowStyle = false
      }
    },
    applyTrackPointVisibility(opts) {
      if (!opts) return
      const radius = this.hideTrackPoints ? 0 : 2.5
      const hoverRadius = this.hideTrackPoints ? 0 : 3.5
      if (opts.keyPointStyle) opts.keyPointStyle.radius = radius
      if (opts.keyPointHoverStyle) opts.keyPointHoverStyle.radius = hoverRadius
      if (opts.keyPointOnSelectedPathLineStyle) opts.keyPointOnSelectedPathLineStyle.radius = radius
    },
    applyPathLineRenderVisibility() {
      const ins = this.pathSimplifierIns
      if (!ins || typeof ins.getRenderOptions !== 'function') return
      const o = ins.getRenderOptions()
      if (!o || !o.pathLineStyle) return
      if (this.hidePathLineOnly) {
        const kp = o.keyPointOnSelectedPathLineStyle
        const pl = o.pathLineStyle
        const lineAlreadyHidden =
          Number(pl.lineWidth) === 0 && pl.strokeStyle === PATH_LINE_HIDE.strokeStyle
        if (!lineAlreadyHidden) {
          this._pathLineBk = {
            pl: { ...pl },
            ph: o.pathLineHoverStyle ? { ...o.pathLineHoverStyle } : null,
            ps: o.pathLineSelectedStyle ? { ...o.pathLineSelectedStyle } : null,
            da: o.dirArrowStyle ? { ...o.dirArrowStyle } : null,
            kp: kp ? { ...kp } : null
          }
        }
        Object.assign(o.pathLineStyle, PATH_LINE_HIDE)
        if (o.pathLineHoverStyle) Object.assign(o.pathLineHoverStyle, PATH_LINE_HIDE)
        if (o.pathLineSelectedStyle) Object.assign(o.pathLineSelectedStyle, PATH_LINE_HIDE)
        if (o.dirArrowStyle) {
          o.dirArrowStyle.lineWidth = 0
          o.dirArrowStyle.strokeStyle = PATH_LINE_HIDE.strokeStyle
        }
        if (kp) {
          kp.lineWidth = 0
          kp.strokeStyle = PATH_LINE_HIDE.strokeStyle
          kp.borderWidth = 0
          kp.borderStyle = PATH_LINE_HIDE.borderStyle
        }
      } else if (this._pathLineBk) {
        const b = this._pathLineBk
        Object.assign(o.pathLineStyle, b.pl)
        if (o.pathLineHoverStyle && b.ph) Object.assign(o.pathLineHoverStyle, b.ph)
        if (o.pathLineSelectedStyle && b.ps) Object.assign(o.pathLineSelectedStyle, b.ps)
        if (o.dirArrowStyle && b.da) Object.assign(o.dirArrowStyle, b.da)
        if (o.keyPointOnSelectedPathLineStyle && b.kp) Object.assign(o.keyPointOnSelectedPathLineStyle, b.kp)
        this.applyPathSimplifierStrokeColors(o)
        this.applyUniformTrajectoryPathLineWidths(o)
      }
      try {
        if (typeof ins.renderLater === 'function') ins.renderLater()
        else if (typeof ins.render === 'function') ins.render()
      } catch (e) {
        /* ignore */
      }
    },
    setPathLineOnlyHidden(hidden) {
      if (this.trackData && this.trackData.segmentKeys) {
        this.hidePathLineOnly = !!hidden
        this.applyPathLineRenderVisibility()
        return true
      }
      if (!this.pathSimplifierIns) return false
      this.hidePathLineOnly = !!hidden
      this.applyPathLineRenderVisibility()
      this.refreshSpeedTrajectoryOverlay()
      return true
    },
    /** 起终文字随工具开关显示；未结束运单额外隐藏轨迹末点图标和文字。 */
    syncEndpointMarkersVisibility() {
      const has = this.traceList && this.traceList.length > 0;
      if (this.markerStart && this.markerEnd) {
        has ? this.markerStart.show() : this.markerStart.hide();
        has && !this.hideTrackEndMarker ? this.markerEnd.show() : this.markerEnd.hide();
      }
      if (!this.markerStartLabel || !this.markerEndLabel) return;
      const showLabel = has && !this.endpointMarkersHidden;
      this.markerStartLabel[showLabel ? 'show' : 'hide']();
      this.markerEndLabel[showLabel && !this.hideTrackEndMarker ? 'show' : 'hide']();
    },
    setEndpointMarkersHidden(hidden) {
      if (!this.markerStartLabel || !this.markerEndLabel) return false
      this.endpointMarkersHidden = !!hidden
      this.syncEndpointMarkersVisibility()
      return true
    },
    async updateEndpointLabels(firstPoint, lastPoint) {
      this.paintEndpointLabelsPreview(firstPoint, lastPoint);
      await this.resolveEndpointLabelsAddress(firstPoint, lastPoint);
      this.syncEndpointMarkersVisibility();
    },
    paintEndpointLabelsPreview(firstPoint, lastPoint) {
      if (!this.mapInstance) return;
      if (!firstPoint || !lastPoint) return;
      if (!this.markerStartLabel || !this.markerEndLabel) return;
      const startLonLat = [firstPoint.lon, firstPoint.lat];
      const endLonLat = [lastPoint.lon, lastPoint.lat];
      const startTime = this.formatPopupUpdateTime(firstPoint);
      const endTime = this.formatPopupUpdateTime(lastPoint);
      const startFallback = `${Number(firstPoint.lon).toFixed(6)},${Number(firstPoint.lat).toFixed(6)}`;
      const endFallback = `${Number(lastPoint.lon).toFixed(6)},${Number(lastPoint.lat).toFixed(6)}`;

      this.markerStartLabel.setPosition(startLonLat);
      this.markerEndLabel.setPosition(endLonLat);
      this.markerStartLabel.setText(`起：${startTime}\n${startFallback}`);
      this.markerEndLabel.setText(`终：${endTime}\n${endFallback}`);
      this.setTripCardSyncedLines(startFallback, endFallback);
      this.syncEndpointMarkersVisibility();
    },
    scheduleClipRangeEndpointGeocode(firstPoint, lastPoint) {
      schedulePlaybackRangeEndpointGeocode(
        this,
        firstPoint,
        lastPoint,
        (fp, lp, gen) => {
          this.resolveEndpointLabelsAddress(fp, lp, gen)
            .catch(() => {})
            .finally(() => this.syncEndpointMarkersVisibility());
        }
      );
    },
    async resolveEndpointLabelsAddress(firstPoint, lastPoint, gen) {
      if (!this.mapInstance || !firstPoint || !lastPoint) return;
      if (!this.markerStartLabel || !this.markerEndLabel) return;
      const startLonLat = [firstPoint.lon, firstPoint.lat];
      const endLonLat = [lastPoint.lon, lastPoint.lat];
      const startTime = this.formatPopupUpdateTime(firstPoint);
      const endTime = this.formatPopupUpdateTime(lastPoint);

      try {
        const addr = await this.getAddressByLonLat(startLonLat);
        if (isPlaybackRangeGeocodeGenerationStale(this, gen)) return;
        this.markerStartLabel.setText(`起：${startTime}\n${addr}`);
        this.setTripCardSyncedLines(addr != null ? String(addr) : '', undefined);
      } catch (e) {
        /* ignore */
      }
      try {
        const addr = await this.getAddressByLonLat(endLonLat);
        if (isPlaybackRangeGeocodeGenerationStale(this, gen)) return;
        this.markerEndLabel.setText(`终：${endTime}\n${addr}`);
        this.setTripCardSyncedLines(undefined, addr != null ? String(addr) : '');
      } catch (e) {
        /* ignore */
      }
    },

    // 初始化起点/终点/停车点图标与 Marker 实例（起终点会 add 到地图并默认 hide）
    initStartEndMarkers() {
      this.markerStartIcon = createTrackMarkerIcon(
        this.AMap,
        startIcon,
        TRACK_ENDPOINT_ICON_W,
        TRACK_ENDPOINT_ICON_H
      );
      this.markerEndIcon = createTrackMarkerIcon(
        this.AMap,
        endIcon,
        TRACK_ENDPOINT_ICON_W,
        TRACK_ENDPOINT_ICON_H
      );
      this.markerStopIcon = createTrackMarkerIcon(
        this.AMap,
        stopIcon,
        TRACK_ENDPOINT_ICON_W,
        TRACK_ENDPOINT_ICON_H
      );
      const startMarkerIcon = this.markerStartIcon;
      const endMarkerIcon = this.markerEndIcon;

      this.markerStart = new this.AMap.Marker({
        icon: startMarkerIcon,
        zIndex: 210,
        offset: new this.AMap.Pixel(-15, -35)
      });
      this.markerStart.hide();
      this.mapInstance.add(this.markerStart);
      this.markerStart.on("click", () => {
        const startPoint = this.traceList && this.traceList.length ? this.traceList[0] : null;
        if (!startPoint) return;
        this.openEndpointInfo("start", startPoint, [startPoint.lon, startPoint.lat]);
      });

      this.markerEnd = new this.AMap.Marker({
        icon: endMarkerIcon,
        zIndex: 210,
        offset: new this.AMap.Pixel(-15, -35)
      });
      this.markerEnd.hide();
      this.mapInstance.add(this.markerEnd);
      this.markerEnd.on("click", () => {
        const endPoint = this.traceList && this.traceList.length ? this.traceList[this.traceList.length - 1] : null;
        if (!endPoint) return;
        this.openEndpointInfo("end", endPoint, [endPoint.lon, endPoint.lat]);
      });

      // 起终点文字条（在图标下方）
      // 起终点地址条层级最高（高于停车时长标签 21），避免被「XX分钟」遮挡
      this.markerStartLabel = new this.AMap.Text({
        text: "",
        zIndex: 220,
        offset: new this.AMap.Pixel(100, 80),
        style: {
          "background-color": "rgba(255,255,255,0.5)",
          border: "0.0625rem solid rgba(70,124,253,0.35)",
          "border-radius": "0.375rem",
          padding: "0.375rem 0.625rem",
          "box-shadow": "0 0.125rem 0.5rem rgba(0,0,0,0.10)",
          color: "#333",
          "font-weight": "bold",
          "font-size": "0.75rem",
          "line-height": "1.4",
          "white-space": "pre-line",
          "text-align": "left",
          "min-width": "160px"
        }
      });
      this.markerEndLabel = new this.AMap.Text({
        text: "",
        zIndex: 220,
        offset: new this.AMap.Pixel(-100, 60),
        style: {
          "background-color": "rgba(255,255,255,0.5)",
          border: "0.0625rem solid rgba(70,124,253,0.35)",
          "border-radius": "0.375rem",
          padding: "0.375rem 0.625rem",
          "box-shadow": "0 0.125rem 0.5rem rgba(0,0,0,0.10)",
          color: "#333",
          "font-weight": "bold",
          "font-size": "0.75rem",
          "line-height": "1.4",
          "white-space": "pre-line",
          "text-align": "left",
          "min-width": "160px"
        }
      });
      this.markerStartLabel.hide();
      this.markerEndLabel.hide();
      this.mapInstance.add(this.markerStartLabel);
      this.mapInstance.add(this.markerEndLabel);
    },
    async openEndpointInfo(type, point, lonlat) {
      const o =
        this.endpointDisplayOverride && typeof this.endpointDisplayOverride === 'object'
          ? this.endpointDisplayOverride
          : null;
      const side = type === 'end' ? 'end' : 'start';
      const ov = o && o[side] && typeof o[side] === 'object' ? o[side] : null;
      const ovLon = ov ? Number(ov.lon) : NaN;
      const ovLat = ov ? Number(ov.lat) : NaN;
      // 仅改展示文本，弹窗定位保持原点位
      const [originLon, originLat] = lonlat;
      const lon = Number.isFinite(ovLon) ? ovLon : originLon;
      const lat = Number.isFinite(ovLat) ? ovLat : originLat;
      const ovTime = ov && ov.time != null ? String(ov.time).trim() : '';
      const ovTimeMs = ovTime ? new Date(ovTime.replace(/-/g, '/')).getTime() : NaN;
      const gpsTime = Number.isFinite(ovTimeMs) ? ovTimeMs : point.gpsTime;
      let addr = `${Number(lon).toFixed(6)},${Number(lat).toFixed(6)}`;
      const ovLocRaw = ov && ov.location != null ? String(ov.location).trim() : '';
      if (ovLocRaw && isValidResolvedAddress(ovLocRaw)) {
        addr = ovLocRaw;
      } else {
        try {
          const resolved = await this.getAddressByLonLat(lonlat);
          if (resolved) addr = resolved;
        } catch (err) {
          console.error('起终点地址获取失败：', err);
        }
      }
      this.showInfoWindow({
        lonlat,
        infoData: {
          ...point,
          lon,
          lat,
          gpsTime,
          popupType: type,
          addr,
          vno: this.getInfoPlateNo()
        }
      });
    },
    async openParkingInfo(parking) {
      const lonlat = [parking.beginLon, parking.beginLat];
      const pickParkingAddr = () => {
        const parts = [parking.parkAdr, parking.address, parking.location];
        for (let i = 0; i < parts.length; i++) {
          const x = parts[i];
          if (x == null || String(x).trim() === '') continue;
          const t = String(x).trim();
          if (isValidResolvedAddress(t)) return t;
        }
        return '';
      };
      let addr = pickParkingAddr();
      if (!addr) {
        try {
          addr = await this.getAddressByLonLat(lonlat);
        } catch (err) {
          console.error('停车点地址获取失败：', err);
        }
      }
      if (!this.mapInstance || this.parkingMarkersHidden
        || !this.showTrackParkingDetailList.some(item => this.parkingEventKey(item) === this.parkingEventKey(parking))) return
      if (!addr) {
        addr = `${Number(parking.beginLon).toFixed(6)},${Number(parking.beginLat).toFixed(6)}`;
      }
      const newData = this.traceList.find((item) => item.gtm === parking.endGtm);
      this.showInfoWindow({
        lonlat,
        infoData: {
          lon: parking.beginLon,
          lat: parking.beginLat,
          agl: parking.agl != null && parking.agl !== '' ? parking.agl : parking.angle,
          spd: newData && newData.spd != null ? newData.spd : 0,
          // 停车事件海拔：优先 parking 自带，其次由 click 时注入的 tracePointHgt
          hgt:
            parking && parking.hgt != null && parking.hgt !== ''
              ? parking.hgt
              : parking && parking.tracePointHgt != null && parking.tracePointHgt !== ''
                ? parking.tracePointHgt
                : null,
          gpsStatus: parking.gpsStatus,
          popupType: 'parking',
          parkingBeginGpsTime: parking.beginGpsTime == null ? parking.startGtm : parking.beginGpsTime,
          parkingStartTime: this.formatParkingDetailTime(parking.startGtm || parking.beginGpsTime),
          parkingEndTime: this.formatParkingDetailTime(parking.endGtm || parking.endGpsTime),
          parkingDuration: parking.duration || this.formatParkingDuration(parking.parkTime),
          gpsTime: parking.endGpsTime || parking.endGtm || parking.beginGpsTime || parking.startGtm,
          addr,
          vno: this.getInfoPlateNo()
        }
      });
    },
    /** 点击停车点：车标预览到该点，播放进度不变；若正在播放则暂停 */
    handleParkingPointPreview(parking, pointIndex) {
      if (!parking) return;
      const lon = Number(parking.beginLon);
      const lat = Number(parking.beginLat);
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
      if (!this.navg) {
        this.openParkingInfo(parking);
        return;
      }
      const wasPlaying = this.playStatus === 'start';
      if (wasPlaying) {
        try { this.navg.pause(); } catch (e) { /* ignore */ }
        this.pauseDistance = this.navg.getMovedDistance();
        this.playStatus = 'pause';
      }
      const savedPauseDistance = this.pauseDistance;
      const savedSliderVal = this.sliderVal;
      let previewAngle = 0;
      const targetIdx =
        pointIndex != null && pointIndex >= 0
          ? Math.min(pointIndex, this.traceList.length - 1)
          : -1;
      if (targetIdx >= 0) {
        this.navg.stop();
        this.navg.start();
        this.navg.pause();
        this.navg.moveToPoint(targetIdx);
        if (typeof this.navg.getCursorAngleRadians === 'function') {
          const rad = this.navg.getCursorAngleRadians();
          previewAngle = ((-90 * Math.PI) / 180 + rad) * (180 / Math.PI);
        }
      }
      this.restoreNavigatorToPauseDistance(savedPauseDistance);
      this.pauseDistance = savedPauseDistance;
      this.sliderVal = savedSliderVal;
      this.tracePointPreviewActive = !this.disablePointPreviewVehicle;
      if (this.disablePointPreviewVehicle) this.clearPreviewVehicleMarker();
      else this.showPreviewVehicleMarker(lon, lat, previewAngle);
      this.pathSimplifierIns && this.pathSimplifierIns.renderLater();
      this.$emit('nav-progress', {
        ratio: this.distanceAll > 0 ? savedPauseDistance / this.distanceAll : 0,
        navPos: { lon, lat },
        isPlaying: false,
        previewOnly: true
      });
      this.openParkingInfo(parking);
    },

    // 渲染停车点 Marker：根据停车事件经纬度创建 marker，并绑定点击事件定位到对应轨迹点
    renderParkingMarkers() {
      this.mapInstance.remove(this.markerStopList);
      this.markerStopList = [];
      this.parkingMarkerRecords = [];

      this.showTrackParkingDetailList.forEach(parking => {
        const durationText = this.formatParkingDuration(parking.parkTime);
        const marker = new this.AMap.Marker({
          icon: this.markerStopIcon,
          zIndex: 20,
          position: [parking.beginLon, parking.beginLat],
          offset: new this.AMap.Pixel(-13, -33)
        });

        // 找到该停车点在轨迹点列表中的位置（按停车结束时间与轨迹更新时间匹配）
        const normalizeParkingUpdateMs = (p) => {
          if (!p) return null;
          const raw = p.endGpsTime != null ? p.endGpsTime : p.endGtm != null ? p.endGtm : null;
          if (raw == null || raw === "") return null;
          if (typeof raw === "number" && Number.isFinite(raw)) return raw < 1e12 ? raw * 1000 : raw;
          const s = String(raw).trim();
          if (!s) return null;
          if (/^\d+$/.test(s)) {
            const n = Number(s);
            if (!Number.isFinite(n)) return null;
            return n < 1e12 ? n * 1000 : n;
          }
          const m = s.match(/^(\d{8})\/(\d{6})$/);
          if (m) {
            const y = m[1].slice(0, 4);
            const mo = m[1].slice(4, 6);
            const d = m[1].slice(6, 8);
            const hh = m[2].slice(0, 2);
            const mm = m[2].slice(2, 4);
            const ss = m[2].slice(4, 6);
            const ts = new Date(`${y}-${mo}-${d} ${hh}:${mm}:${ss}`).getTime();
            return Number.isFinite(ts) ? ts : null;
          }
          return null;
        };
        const updateMs = normalizeParkingUpdateMs(parking);
        const pointIndex = this.traceList.findIndex((point) => {
          if (!point) return false;
          const ptMs = point.gpsTime != null && point.gpsTime !== "" ? Number(point.gpsTime) : NaN;
          const sameTime = updateMs != null && Number.isFinite(ptMs) ? ptMs === updateMs : false;
          if (sameTime) return true;
          if (updateMs == null && parking.endGtm && point.gtm) {
            return String(point.gtm).trim() === String(parking.endGtm).trim();
          }
          return false;
        });
        // 停车点点击：预览到该点，不改变播放进度
        marker.on("click", () => {
          this.handleParkingPointPreview(parking, pointIndex);
        });

        this.markerStopList.push(marker);

        const durationLabel = new this.AMap.Text({
          position: [parking.beginLon, parking.beginLat],
          zIndex: 21,
          text: durationText,
          offset: new this.AMap.Pixel(-16, 4),
          style: {
            'background-color': '#ffffff',
            'color': '#000000',
            'border': '0.0625rem solid #e53935',
            'padding': '0.25rem 0.625rem',
            'border-radius': '62.4375rem',
            'font-size': '0.75rem',
            'text-align': 'center',
            'white-space': 'nowrap',
            'font-family': 'sans-serif',
            'box-sizing': 'border-box'
          }
        });
        durationLabel.on("click", () => {
          this.handleParkingPointPreview(parking, pointIndex);
        });
        this.markerStopList.push(durationLabel);
        this.parkingMarkerRecords.push({ marker, label: durationLabel, pointIndex });
      });

      // 批量添加到地图
      this.mapInstance.add(this.markerStopList);
      this.syncParkingMarkersVisibility()
      this.raiseEndpointLabelsAboveParking()
    },
    /** 停车点添加后再次抬升起终点地址条，确保盖住「XX分钟」 */
    raiseEndpointLabelsAboveParking() {
      const raise = (m, z) => {
        if (!m) return
        try {
          if (typeof m.setzIndex === 'function') m.setzIndex(z)
          else if (typeof m.setOptions === 'function') m.setOptions({ zIndex: z })
        } catch (e) {
          /* ignore */
        }
      }
      raise(this.markerStart, 210)
      raise(this.markerEnd, 210)
      raise(this.markerStartLabel, 220)
      raise(this.markerEndLabel, 220)
    },

    // 初始化地图插件：自定义信息窗 + 地理编码器
    initMapPlugins() {
      this.infoWindow = new this.AMap.InfoWindow({
        isCustom: true,
        // 统一：底部居中 + 小角标指向定位点
        anchor: "bottom-center",
        offset: new this.AMap.Pixel(0, -14)
      });

      this.geocoder = new this.AMap.Geocoder({
        city: "全国",
        radius: 1000
      });
    },

    // 创建比例尺控件（位置与原页面保持一致）
    createScaleControl() {
      return new this.AMap.Scale({
        position: { left: "1.875rem", bottom: "5.75rem" }
      });
    },

    // 当前页已切换为单一轨迹数据源，不再依赖 vehicleList 映射
  }
};
</script>

<style>
.amap-marker .amap-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.infoWidowBox {
  background: #fff;
  border-radius: 0.625rem;
  box-shadow: 0 0.125rem 0.5rem rgba(0, 0, 0, .12);
  height: auto;
  width: 18.75rem;
  overflow: visible;
  padding: 0.75rem 1rem;
  position: relative;
}

/* 底部居中小角标（白底 + 阴影），指向定位点 */
.infoWidowBox::before {
  content: "";
  position: absolute;
  left: 50%;
  bottom: -0.75rem;
  transform: translateX(-50%);
  border-width: 0.75rem 0.75rem 0 0.75rem;
  border-style: solid;
  border-color: rgba(0, 0, 0, 0.14) transparent transparent transparent;
  filter: blur(0.0125rem);
}

.infoWidowBox::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: -0.5625rem;
  transform: translateX(-50%);
  border-width: 0.6875rem 0.6875rem 0 0.6875rem;
  border-style: solid;
  border-color: #fff transparent transparent transparent;
}

.infoWidowBox .statusBarBox {
  background: #999;
  border-radius: 0.25rem 0.25rem 0 0;
  color: #fff;
  display: none;
  font-size: 1rem;
  height: 2.5rem;
  line-height: 2.5rem;
  position: relative;
  text-align: center;
  width: 100%
}

.infoWidowBox .statusBarBox .statusBar {
  background: #ff3b30;
  border-radius: 0.25rem 0.25rem 0 0;
  height: 100%;
  left: 0;
  max-width: 100%;
  position: absolute;
  top: 0;
  width: 0;
  z-index: 1
}

.infoWidowBox .statusBarBox .statusLabel {
  position: relative;
  z-index: 9
}

.infoWidowBox .statusBarBox .statusTip {
  background: url(./assets/img/icon-17.ce45740.png) no-repeat;
  background-size: 100% 100%;
  display: none;
  left: 22.125rem;
  padding: 0.625rem 1.5625rem;
  position: absolute;
  top: 0;
  white-space: nowrap
}

.infoWidowBox .statusBarBox .statusTip .statusTip1 {
  color: #fff;
  font-size: 0.9375rem;
  font-weight: 400;
  height: 1.3125rem;
  line-height: 1.3125rem;
  text-align: left
}

.infoWidowBox .statusBarBox .statusTip .statusTip2 {
  color: #fff;
  font-size: 0.75rem;
  font-weight: 400;
  height: 1.125rem;
  line-height: 1.125rem;
  text-align: left
}

.infoWidowBox .titleBox {
  align-items: center;
  border-bottom: 0.0625rem solid #ebeef5;
  display: flex;
  justify-content: flex-start;
  position: relative;
  width: 100%;
  margin-bottom: 0.625rem;
}

.infoWidowBox .titleBox .vehInfo {
  display: flex;
  flex-direction: column
}

.infoWidowBox .titleBox .vehInfo .vehNumber {
  color: #303133;
  display: block;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1;
  margin-top: 0;
  white-space: nowrap;
  width: auto
}

.infoWidowBox .titleBox .vehStatus {
  color: #f56c6c;
  font-size: 0.875rem;
  font-weight: 600;
  margin-left: auto;
  margin-right: 0.625rem;
}

.infoWidowBox .titleBox .vehStatus.is-moving {
  color: #52c41a;
}

.infoWidowBox .titleBox .vehStatus.is-stop {
  color: #f56c6c;
}

.infoWidowBox .titleBox .vehStatus.is-start {
  color: #52c41a;
}

.infoWidowBox .titleBox .vehStatus.is-end {
  color: #f56c6c;
}

.infoWidowBox .titleBox .vehStatus.is-parking {
  color: #909399;
}

.infoWidowBox .titleBox .infoCloseBtn {
  color: #c0c4cc;
  cursor: pointer;
  font-size: 1.75rem;
  line-height: 1;
  margin-top: -0.0625rem;
  user-select: none;
}

.infoWidowBox .titleBox .vehInfo .vehInfo {
  color: rgba(0,0,0,.65);
  font-size: 0.875rem;
  font-weight: 400;
  height: 1.25rem
}

.infoWidowBox .titleBox .speedBox {
  display: none;
}

.infoWidowBox .markerContent {
  color: #606266;
  display: flex;
  flex-direction: column;
  font-size: 0.75rem;
  line-height: 1.55;
}

.infoWidowBox .markerContent .markerLine {
  display: flex;
  margin-bottom: 0
}

.infoWidowBox .markerContent .markerLine .markerS1 {
  color: #606266;
  width: 4.5rem
}

.infoWidowBox .markerContent .markerLine .markerS2 {
  color: #303133;
  flex: 1
}

.infoWidowBox .markerContent .markerLine .markerS2.markerInline {
  letter-spacing: 0.0125rem;
}

.infoWidowBox .markerContent .markerLine .markerS2.markerAddr {
  color: #1677ff;
  word-break: break-all;
}

.infoWidowBox .markerContent .markerLine.markerLineSplit {
  margin-top: 0.125rem;
}


.page-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.mapContainer {
  height: 100%;
  width: 100%
}

/* 自定义信息窗：放开外层裁剪，并隐藏高德默认尖角，避免覆盖我们的小角 */
.amap-info-content {
  overflow: visible !important;
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  padding: 0 !important;
}

.amap-info-sharp {
  display: none !important;
}
</style>
